import { v4 as uuidv4 } from 'uuid'
import moment from 'moment'
import { useEffect, useRef } from 'react'
import { useInboxStore } from '../../../stores/inbox/useInboxStore'
import { useAuthStore } from '../../../stores/auth/useAuthStore'
import { useAppActions } from '../../../stores/application/useAppActions'
import { useConversationsRequests } from '../../apis/conversations/useConversationsRequests'
import { useExtensionRequest } from '../../apis/extension/useExtensionRequest'
import { useConnectionsRequest } from '../../apis/connections/useConnectionsRequest'
import { useAssistantRequest } from '../../apis/assistant/useAssistantRequest'
import { useTagsRequest } from '../../apis/tags/useTagsRequest'
import { useTagsActions } from '../../../stores/tags/useTagsActions'
import { useTagsStore } from '../../../stores/tags/useTagsStore'
import { IConnection } from '../../../interfaces/models/connection'
import { setInboxTagsSyncFromConnectionsListener, notifyInboxConnectionsUpdatedFromConnections } from '../../inboxTagsSyncFromConnections'
import { patchConversationFromConnection, patchConversationsFromConnections } from '../../inboxConnectionSync'
import { useConnectionsHandlers } from '../connections/useConnectionsHandlers'
import { canSyncSalesNavigatorFromExtension, evaluateSalesNavigatorGuard, SALES_NAVIGATOR_DETECTED_PLAN, SALES_NAVIGATOR_GUARD_MESSAGE, REGULAR_LINKEDIN_PLAN, type ISalesNavigatorData } from '../../inbox/salesNavigatorAccess'
import { useAppStore } from '../../../stores/application/useAppStore'
import { useInboxSettingsStore } from '../../../stores/inbox/useInboxSettingsStore'
import {
    conversationMatchesInboxLabel,
    getConversationTagLabel,
    getMainInboxEnabledAiTagLabels,
    isConversationTagInboxTab,
    isStaleRelabelTagStatus,
} from '../../../stores/inbox/inboxConversationTagDefaults'

/** Shared across all `useInboxHandlers()` instances — avoids duplicate SN detection on inbox open. */
let loadSalesNavigatorUserInFlight: Promise<void> | null = null

/** Shared across all useInboxHandlers() call sites in the inbox tree. */
const dismissedFromLabelTabRef = { current: new Map<string, Set<string>>() }
const awaitingRelabelOnTabRef = { current: new Map<string, string>() }
/** conversationId → AI label snapshot captured when the user sent a reply. */
const tagsBeforeRelabelRef = { current: new Map<string, string | null>() }

export const useInboxHandlers = () => {
    const { store: inboxStore, dispatch: inboxDispatch } = useInboxStore()
    const { store: { conversationTags: conversationTagSettings } } = useInboxSettingsStore()
    const { store: { user, workspace } } = useAuthStore()
    const { store: { extensionInstalled } } = useAppStore()
    const { setToast, setSpinner } = useAppActions()
    const { setAllTags: setAllTagsStore } = useTagsActions()
    const { store: { allTags: existingAllTagsStore } } = useTagsStore()

    const {
        paginateConversations,
        deleteConversation: deleteConversationRequest,
        updateConversations,
        starConversation: starConversationRequest,
        starConversations: starConversationsRequest,
        pinConversation: pinConversationRequest,
        pinConversations: pinConversationsRequest,
        getFilteringOptions,
        getInboxQueryParams,
        applyTagsToConversations: applyTagsRequest,
        removeTagsFromConversations: removeTagsRequest,
        applyRemoveTagsFromConversations: applyRemoveTagsRequest,
        snoozeConversations: snoozeConversationsRequest,
        getConversationsTagStatus,
    } = useConversationsRequests()

    const {
        getConnectionByPublicIdentifier,
        getConnectionByProfileId,
        getConnectionByProfileDocumentId,
        addToLeadDelta,
    } = useConnectionsRequest()
    const { saveConversationArchive } = useAssistantRequest()
    const { getTagsPaginate } = useTagsRequest()

    const {
        getMessagesFromConversation,
        extractLinkedinProfileHtml,
        ldAddNewConnection,
        /** Same extension call as `syncConversationsHandler` in useAuthHandlers (HomeSection, doAutomaticSync ~12h). */
        syncConversations,
        syncSalesNavigatorConversations,
        createOrSendMessage,
        sendMessageInConversation,
        inboxMarkAsSeen,
        inboxSyncLinkedinConversationStar,
        inboxArchiveConversation,
        inboxArchiveConversations,
        inboxDeleteConversation,
        visitLinkedInConversation,
        visitLinkedInProfile,
        getLoggedInLIUserForInbox,
    } = useExtensionRequest()

    const { resolveLinkedinProfileForInboxEnrichHandler, runSingleConnectionEnrichmentHandler } = useConnectionsHandlers()

    const {
        conversations,
        selectedConversation,
        selectedConversations,
        allConversationsFetched,
        lastFetchedConversation,
        fetchingConversations,
        fetchingMoreConversations,
        messages,
        allMessagesFetched,
        fetchingMessages,
        fetchingMoreMessages,
        pinned,
        starred,
        readed,
        tags,
        archived,
        query,
        filteringTags,
        selectedProfile,
        selectedConnection,
        pinnedConversationsArray,
        conversationDetails,
        messageAttachments,
        appliedTemplate,
        lastMessagedFrom,
        lastMessagedTo,
        lastMessagedOnlyLast,
        conversationStatus,
        conversationOwners,
        linkedinNextCursor,
        salesNavigatorNextCursor,
        selectedTab,
        activeInboxLabel,
        salesNavigatorData,
    } = inboxStore

    const isSalesNavigatorTab = selectedTab === 'SalesNavigator'
    const isSalesUser = canSyncSalesNavigatorFromExtension(salesNavigatorData)
    const isSalesNavigatorActive = isSalesNavigatorTab && isSalesUser

    const shouldUseSalesMessagingApi = (conversation?: { isSales?: boolean } | null) =>
        isSalesNavigatorActive || !!conversation?.isSales

    const inboxTagSyncRef = useRef({ conversations, selectedConversation, pinnedConversationsArray })
    inboxTagSyncRef.current = { conversations, selectedConversation, pinnedConversationsArray }
    const latestInboxStoreRef = useRef(inboxStore)
    latestInboxStoreRef.current = inboxStore

    useEffect(() => {
        dismissedFromLabelTabRef.current.clear()
        awaitingRelabelOnTabRef.current.clear()
        tagsBeforeRelabelRef.current.clear()
    }, [activeInboxLabel])

    useEffect(() => {
        dismissedFromLabelTabRef.current.clear()
        awaitingRelabelOnTabRef.current.clear()
        tagsBeforeRelabelRef.current.clear()
    }, [activeInboxLabel])

    const isDismissedFromLabelTab = (conversationId: string, label: string | undefined) => {
        if (!label || !isConversationTagInboxTab(label)) return false
        return dismissedFromLabelTabRef.current.get(label)?.has(conversationId) ?? false
    }

    const dismissFromLabelTab = (conversationId: string, label: string) => {
        if (!dismissedFromLabelTabRef.current.has(label)) {
            dismissedFromLabelTabRef.current.set(label, new Set())
        }
        dismissedFromLabelTabRef.current.get(label)!.add(conversationId)
    }

    const resolveInboxPremiumFlag = async (): Promise<boolean> => {
        try {
            const li: any = await getLoggedInLIUserForInbox()
            if (li?.isPremium != null) return !!li.isPremium
        } catch {
            /* fall through */
        }
        if (user?.linkedinData?.isPremium != null) return !!user.linkedinData.isPremium
        return false
    }

    const probeSalesNavigatorSession = async (): Promise<boolean> => {
        try {
            const raw = await syncSalesNavigatorConversations({
                filter: { archived: 'All', read: 'All' },
            })
            return raw !== false && raw != null && !(raw instanceof Error)
        } catch {
            return false
        }
    }

    const loadSalesNavigatorUser = async (): Promise<void> => {
        if (!extensionInstalled || !user?.linkedinData) return
        if (loadSalesNavigatorUserInFlight) return loadSalesNavigatorUserInFlight

        const existing = latestInboxStoreRef.current.salesNavigatorData
        if (canSyncSalesNavigatorFromExtension(existing) && existing?.isPremium != null) return

        loadSalesNavigatorUserInFlight = (async () => {
            try {
                const isPremium = await resolveInboxPremiumFlag()
                let payload: ISalesNavigatorData = {
                    isPremium,
                    userPlans: [REGULAR_LINKEDIN_PLAN],
                    SNProfile: null,
                }

                if (isPremium) {
                    const hasSnAccess = await probeSalesNavigatorSession()
                    if (hasSnAccess) {
                        payload = {
                            isPremium: true,
                            userPlans: [SALES_NAVIGATOR_DETECTED_PLAN],
                            SNProfile: {},
                        }
                    }
                }

                inboxDispatch({ type: 'set-sales-navigator-data', payload })
            } catch (e) {
                console.warn('[Inbox] loadSalesNavigatorUser failed', e)
            } finally {
                loadSalesNavigatorUserInFlight = null
            }
        })()

        return loadSalesNavigatorUserInFlight
    }

    /** Block SN tab actions before extension APIs run (parity with extension isSalesUser / getUserSalesId guards). */
    const guardSalesNavigatorAction = (): boolean => {
        if (!isSalesNavigatorTab) return true
        const reason = evaluateSalesNavigatorGuard(user?.linkedinData, latestInboxStoreRef.current.salesNavigatorData ?? salesNavigatorData)
        if (reason === 'ok') return true
        setToast({ type: 'info', message: SALES_NAVIGATOR_GUARD_MESSAGE[reason] })
        return false
    }
    /**
     * Paginate scope for inbox list API.
     * - Sales Navigator tab / label → only SN threads (`isSales: true`).
     * - All messages → no isSales filter (include LinkedIn + Sales Nav in one list).
     * - Other Inbox labels (Main inbox, AI tags, snoozed, …) → LinkedIn only (`isSales: false`).
     */
    const resolvePaginateIsSales = (): boolean | undefined => {
        if (isSalesNavigatorTab) return true
        const label = latestInboxStoreRef.current.activeInboxLabel ?? activeInboxLabel
        if (label === 'allMessages' || label === 'snoozed') return undefined
        if (
            label === 'mainInbox' &&
            conversationTagSettings.inbox_conversation_tag_sales_navigator !== false
        ) {
            return undefined
        }
        return false
    }

    const paginateInboxListPage = (args: { page: number; filters?: Record<string, unknown> | undefined }) => {
        const isSales = resolvePaginateIsSales()
        return paginateConversations({
            ...args,
            ...(isSales !== undefined ? { isSales } : {}),
        })
    }
    const filteringTagsLoadedRef = useRef(false)
    const filteringTagsLoadingRef = useRef(false)

    /**
     * List fetch generation: bumped on each `getConversationsFirstPage` (tab/filter) so stale
     * paginate responses are ignored. Separate from background sync session below.
     */
    const linkedinInboxDrainGenerationRef = useRef(0)
    /** Full regular↔sales LI sync runs once per inbox visit; not restarted on tab switch. */
    const inboxFullBackgroundSyncStartedRef = useRef(false)
    const backgroundSyncSessionRef = useRef(0)
    const linkedinInboxDrainRunningRef = useRef(false)
    const loadMoreConversationsInFlightRef = useRef(false)

    /** Pause between LI pages to reduce rate-limit risk (extension still does heavy work). */
    const LINKEDIN_INBOX_DRAIN_PAGE_DELAY_MS = 300

    /**
     * BulkEditTagsModal lives outside InboxStoreProvider; connections handlers notify here so inbox items update.
     */
    useEffect(() => {
        const applyConnectionsSyncToInbox = (updatedConnections: IConnection[]) => {
            if (!updatedConnections?.length) return
            const { conversations: convs, selectedConversation: sel, pinnedConversationsArray: pinned } = inboxTagSyncRef.current
            if (!convs?.length) return

            const { conversations: nextConversations, changed: conversationsChanged } =
                patchConversationsFromConnections(convs, updatedConnections)

            const pinnedList = pinned ?? []
            const { conversations: nextPinned, changed: pinnedChanged } =
                patchConversationsFromConnections(pinnedList, updatedConnections)

            if (!conversationsChanged && !pinnedChanged) return

            if (conversationsChanged) {
                inboxDispatch({ type: 'set-conversations', payload: nextConversations })
            }
            if (pinnedChanged) {
                inboxDispatch({ type: 'set-pinned-conversations', payload: nextPinned })
            }

            if (sel) {
                let nextSelected = sel
                updatedConnections.forEach((connection) => {
                    nextSelected = patchConversationFromConnection(nextSelected, connection)
                })
                if (nextSelected !== sel) {
                    inboxDispatch({
                        type: 'update-selected-conversation',
                        payload: nextSelected,
                    })
                    if (nextSelected.participants?.length === 1) {
                        inboxDispatch({
                            type: 'set-selected-profile',
                            payload: { ...nextSelected.participants[0] },
                        })
                    }
                }
            }
        }
        setInboxTagsSyncFromConnectionsListener(applyConnectionsSyncToInbox)
        return () => setInboxTagsSyncFromConnectionsListener(null)
    }, [inboxDispatch])

    /**
     * LinkedIn V1 sync returns `nextCursor` either as a plain string or as `{ nextCursor: '...' }`.
     * Normalises it to either a non-empty string (more pages) or `null` (no more).
     */
    const extractNextCursor = (raw: any): string | null => {
        const root =
            raw && typeof raw === 'object' && !Array.isArray(raw) && raw.result !== undefined
                ? raw.result
                : raw
        const inner = root?.nextCursor ?? root
        if (typeof inner === 'string') return inner.length ? inner : null
        if (inner && typeof inner === 'object') {
            const inner2 = inner.nextCursor
            if (typeof inner2 === 'string' && inner2.length) return inner2
        }
        return null
    }

    /**
     * Inbox open/refresh background sync:
     * one call at a time, alternating regular and Sales Navigator.
     * Keeps DB warm regardless of selected tab.
     */
    const runSequentialBackgroundSync = async (
        session: number,
        paginateOptsFrozen: Record<string, unknown> | undefined,
        syncFilter: { archived: string; read: string },
        syncQuery: string,
        listLoadSession: number,
        onListReady?: () => void,
    ) => {
        if (session !== backgroundSyncSessionRef.current) return
        linkedinInboxDrainRunningRef.current = true

        let regularCursor: string | null = ''
        let regularDone = false
        let snCursor: string | null = ''
        let snDone = false
        let listRefreshDone = false

        const refreshListOnceFromDb = async () => {
            if (listRefreshDone || listLoadSession !== linkedinInboxDrainGenerationRef.current) return
            if (session !== backgroundSyncSessionRef.current) return
            const re: any = await paginateInboxListPage({ page: 1, filters: paginateOptsFrozen })
            if (re == null) return
            const refreshed: any[] = re?.conversations ?? []
            if (!refreshed.length) return
            listRefreshDone = true
            inboxDispatch({ type: 'merge-front-page-conversations', payload: refreshed })
            const newPinned = refreshed.filter((c: any) => c.isPinned)
            if (newPinned.length) {
                inboxDispatch({ type: 'set-pinned-conversations', payload: newPinned })
            }
            onListReady?.()
        }

        const syncBatchHasRows = (syncRes: any): boolean =>
            (Array.isArray(syncRes?.conversations) ? syncRes.conversations.length : Array.isArray(syncRes) ? syncRes.length : 0) > 0

        const queryParamsRes = await getInboxQueryParams().catch(() => null)
        const voyagerMessagingGraphQL = queryParamsRes?.data ?? queryParamsRes

        try {
            while (session === backgroundSyncSessionRef.current && (!regularDone || !snDone)) {
                if (!regularDone) {
                    if (!voyagerMessagingGraphQL?.queryId || !voyagerMessagingGraphQL?.profileId) {
                        regularDone = true
                    } else {
                        const syncRes: any = await syncConversations({
                            voyagerMessagingGraphQL,
                            filter: syncFilter,
                            query: syncQuery,
                            nextCursor: regularCursor ?? '',
                        }).catch(() => false as any)
                        if (syncRes === false || syncRes == null) {
                            regularDone = true
                        } else {
                            const nextCursor = extractNextCursor(syncRes)
                            inboxDispatch({ type: 'set-linkedin-next-cursor', payload: nextCursor })
                            regularCursor = nextCursor
                            regularDone = !nextCursor
                            if (syncBatchHasRows(syncRes)) void refreshListOnceFromDb()
                        }
                    }
                }

                if (session !== backgroundSyncSessionRef.current) break

                if (!snDone) {
                    if (!canSyncSalesNavigatorFromExtension(latestInboxStoreRef.current.salesNavigatorData)) {
                        snDone = true
                    } else {
                        const syncRes: any = await syncSalesNavigatorConversations({ nextCursor: snCursor ?? '' }).catch(
                            () => false as any,
                        )
                        if (syncRes === false || syncRes == null) {
                            snDone = true
                        } else {
                            const nextCursor = extractNextCursor(syncRes)
                            inboxDispatch({ type: 'set-sales-navigator-next-cursor', payload: nextCursor })
                            snCursor = nextCursor
                            snDone = !nextCursor
                            if (syncBatchHasRows(syncRes)) void refreshListOnceFromDb()
                        }
                    }
                }

                if (regularDone && snDone) break
                if (session === backgroundSyncSessionRef.current) {
                    await new Promise<void>(resolve => setTimeout(resolve, LINKEDIN_INBOX_DRAIN_PAGE_DELAY_MS))
                }
            }
        } catch (e) {
            console.warn('[Inbox] sequential background sync error', e)
        } finally {
            if (session === backgroundSyncSessionRef.current) {
                linkedinInboxDrainRunningRef.current = false
            }
        }
    }

    const runLinkedInInboxBackgroundDrain = async (
        session: number,
        startCursor: string,
        paginateOptsFrozen: Record<string, unknown> | undefined,
        syncFilter: { archived: string; read: string },
        syncQuery: string,
    ) => {
        if (!startCursor || session !== linkedinInboxDrainGenerationRef.current) return
        linkedinInboxDrainRunningRef.current = true
        let cursor: string | null = startCursor
        try {
            while (cursor && session === linkedinInboxDrainGenerationRef.current) {
                const queryParamsRes = await getInboxQueryParams()
                const voyagerMessagingGraphQL = queryParamsRes?.data ?? queryParamsRes
                if (!voyagerMessagingGraphQL?.queryId || !voyagerMessagingGraphQL?.profileId) {
                    console.warn('[Inbox] background drain: missing queryParams — stopping')
                    break
                }
                const syncRes: any = await syncConversations({
                    voyagerMessagingGraphQL,
                    filter: syncFilter,
                    query: syncQuery,
                    nextCursor: cursor,
                })
                if (syncRes === false || syncRes == null) {
                    console.warn('[Inbox] background drain: syncConversations failed — stopping')
                    break
                }
                const next = extractNextCursor(syncRes)
                inboxDispatch({ type: 'set-linkedin-next-cursor', payload: next })
                cursor = next

                // No re-paginate here: drain pages are older LinkedIn conversations (LinkedIn
                // paginates newest-first) so page=1 in our DB stays unchanged. The initial
                // page=1 fetch (before the drain starts) already has the freshest data.
                // Users see older drain pages when they scroll and trigger loadMoreConversations.
                if (cursor && session === linkedinInboxDrainGenerationRef.current) {
                    await new Promise<void>(resolve => setTimeout(resolve, LINKEDIN_INBOX_DRAIN_PAGE_DELAY_MS))
                }
            }
        } catch (e) {
            console.warn('[Inbox] background drain error', e)
        } finally {
            if (session === linkedinInboxDrainGenerationRef.current) {
                linkedinInboxDrainRunningRef.current = false
            }
        }
    }

    const runSalesNavigatorBackgroundDrain = async (
        session: number,
        startCursor: string,
        paginateOptsFrozen: Record<string, unknown> | undefined,
    ) => {
        if (!startCursor || session !== linkedinInboxDrainGenerationRef.current) return
        linkedinInboxDrainRunningRef.current = true
        let cursor: string | null = startCursor
        try {
            while (cursor && session === linkedinInboxDrainGenerationRef.current) {
                const syncRes: any = await syncSalesNavigatorConversations({ nextCursor: cursor })
                if (syncRes === false || syncRes == null) {
                    console.warn('[Inbox] SN background drain: syncSalesNavigatorConversations failed — stopping')
                    break
                }
                const next = extractNextCursor(syncRes)
                inboxDispatch({ type: 'set-sales-navigator-next-cursor', payload: next })
                cursor = next

                // Same reasoning as LinkedIn classic drain: SN paginates newest-first, so
                // later drain pages are older conversations — page=1 re-paginate is redundant.
                if (cursor && session === linkedinInboxDrainGenerationRef.current) {
                    await new Promise<void>(resolve => setTimeout(resolve, LINKEDIN_INBOX_DRAIN_PAGE_DELAY_MS))
                }
            }
        } catch (e) {
            console.warn('[Inbox] SN background drain error', e)
        } finally {
            if (session === linkedinInboxDrainGenerationRef.current) {
                linkedinInboxDrainRunningRef.current = false
            }
        }
    }

    // ===== FILTER HELPERS =====
    const getFilterParams = (labelOverride?: string) => {
        // Always resolve the label from the ref so we never use a stale closure value.
        // Callers that already validated the label against the ref can pass it explicitly
        // to avoid the (negligible) property access; others get the latest value for free.
        const label = labelOverride ?? latestInboxStoreRef.current.activeInboxLabel ?? activeInboxLabel
        const conversationOwnerIds =
            Array.isArray(conversationOwners) && conversationOwners.some((o: any) => o?.id !== 'all')
                ? conversationOwners.filter((o: any) => o?.id !== 'all').map((o: any) => o.id).join(',')
                : ''
        const labelFilter =
            label === 'mainInbox'
                ? {
                    isMainInbox: true,
                    mainInboxEnabledTags: getMainInboxEnabledAiTagLabels(conversationTagSettings),
                    mainInboxIncludeSalesNav:
                        conversationTagSettings.inbox_conversation_tag_sales_navigator !== false,
                    mainInboxIncludeNewConnection:
                        conversationTagSettings.inbox_conversation_tag_new_connection !== false,
                }
                : label === 'snoozed'
                ? { isSnoozed: true }
                : label === 'newConnection'
                ? { isNewConnection: true }
                : label === 'awaitingReply'
                ? { conversationTags: ['Awaiting reply'] }
                : label?.startsWith('ai:')
                ? { conversationTags: [label.slice(3)] }
                : {}
        return {
            ...labelFilter,
            ...(pinned !== 'All') && (pinned === 'Yes' ? { isPinned: true } : { isPinned: false }),
            ...(starred !== 'All') && (starred === 'Yes' ? { isStarred: true } : { isStarred: false }),
            ...(readed !== 'All') && (readed === 'All Read' ? { isRead: true } : { isRead: false }),
            ...(archived !== 'All') && (archived === 'Yes' ? { isArchived: true } : { isArchived: false }),
            ...(tags.length > 0) && { tags },
            ...(lastMessagedFrom?.length) && { lastMessagedFrom },
            ...(lastMessagedTo?.length) && { lastMessagedTo },
            ...(lastMessagedOnlyLast && { onlyLast: 'yes' }),
            ...(conversationStatus?.value !== 'all') && { conversationStatus },
            ...(conversationOwnerIds.length > 0 && { conversationOwners: conversationOwnerIds }),
        }
    }

    const hasConversationOwnerFilter = () =>
        Array.isArray(conversationOwners) && conversationOwners.some((o: any) => o?.id !== 'all')

    const inboxLabelAllowsExtensionSync = () =>
        activeInboxLabel === 'allMessages' || activeInboxLabel === 'mainInbox'

    const salesNavigatorLabelAllowsExtensionSync = () =>
        activeInboxLabel === 'salesNav' || activeInboxLabel === 'allMessages'

    const canSyncSalesNavigatorExtension = () =>
        canSyncSalesNavigatorFromExtension(
            latestInboxStoreRef.current.salesNavigatorData ?? salesNavigatorData,
        )

    const isAllMessagesTab =
        !isSalesNavigatorTab && (latestInboxStoreRef.current.activeInboxLabel ?? activeInboxLabel) === 'allMessages'

    const hasDbOnlyLabelFilter = () =>
        !!activeInboxLabel &&
        activeInboxLabel !== 'allMessages' &&
        activeInboxLabel !== 'mainInbox' &&
        activeInboxLabel !== 'salesNav'

    const extensionCanRunBackgroundSync = () =>
        pinned === 'All' &&
        starred === 'All' &&
        tags.length === 0 &&
        !lastMessagedFrom?.length &&
        !lastMessagedTo?.length &&
        conversationStatus?.value === 'all' &&
        !lastMessagedOnlyLast &&
        !hasConversationOwnerFilter()

    /**
     * linkedinextension Inbox: classic `inboxGetConversations` (not Voyager-only filters).
     * Extension BG merges LI + synchronizeConversations → DB before returning merged array.
     */
    const extensionCanSyncClassicInbox = () =>
        selectedTab !== 'SalesNavigator' &&
        inboxLabelAllowsExtensionSync() &&
        extensionCanRunBackgroundSync()

    const extensionCanSyncSalesNavigator = () =>
        (isSalesNavigatorActive || isAllMessagesTab) &&
        salesNavigatorLabelAllowsExtensionSync() &&
        extensionCanRunBackgroundSync() &&
        canSyncSalesNavigatorExtension()

    /** LI-sync in extension can take a long time; timeout hit logs separately from `done`. */
    const EXTENSION_INBOX_FETCH_MS = 120_000

    // ===== CONVERSATIONS =====
    const getFilteringTagOptions = async () => {
        if (filteringTagsLoadedRef.current || filteringTagsLoadingRef.current) return
        filteringTagsLoadingRef.current = true
        try {
            // Inbox filter should offer the same full tag list as Contacts, not only tags
            // already present on conversations returned by the inbox filtering endpoint.
            const tagsResult = await getTagsPaginate({ page: 1, pageSize: 1000, searchByName: '' })
            let filteringTagOptions: any[] = tagsResult?.tags ?? []

            if (!filteringTagOptions.length) {
                const result = await getFilteringOptions()
                filteringTagOptions = result?.tags ?? []
            }

            inboxDispatch({ type: 'set-filtering-tags', payload: filteringTagOptions })
            if (filteringTagOptions.length && !existingAllTagsStore?.length) setAllTagsStore(filteringTagOptions)
        } catch (e) {
            console.error(e)
        } finally {
            filteringTagsLoadedRef.current = true
            filteringTagsLoadingRef.current = false
        }
    }

    const salesNavigatorSearchActive =
        isSalesNavigatorActive &&
        typeof query === 'string' &&
        query.trim().length > 0

    const allMessagesSearchActive =
        isAllMessagesTab &&
        typeof query === 'string' &&
        query.trim().length > 0

    /** Same gate as linkedinextension ConversationsList `getConversationsFromLinkedin`. */
    const shouldFetchConversationsFromLinkedin = () =>
        pinned === 'All' &&
        starred === 'All' &&
        tags.length === 0 &&
        !lastMessagedFrom?.length &&
        !lastMessagedTo?.length &&
        conversationStatus?.value === 'all' &&
        !lastMessagedOnlyLast &&
        !hasConversationOwnerFilter() &&
        !hasDbOnlyLabelFilter() &&
        (readed !== 'All' || archived !== 'No' || query.trim().length > 0)

    const extractConversationsFromExtensionSync = (syncRes: any): any[] => {
        if (!syncRes || syncRes === false) return []
        return Array.isArray(syncRes?.conversations)
            ? syncRes.conversations
            : Array.isArray(syncRes)
            ? syncRes
            : []
    }

    const persistFilteredConversationsToDb = async (rows: any[]) => {
        if (!rows.length) return
        await updateConversations({
            type: 'filtered-LD-conversations',
            conversations: rows.map((conversation: any) => ({
                _id: conversation?._id,
                isArchived: conversation?.isArchived,
                isRead: conversation?.isRead,
                isSent: conversation?.isSent,
                dateTimestamp: conversation?.dateTimestamp,
                text: conversation?.text,
            })),
        })
    }

    const conversationMatchesSearchQuery = (conversation: any, searchText: string) => {
        const q = searchText.trim().toLowerCase()
        if (!q) return true
        if (typeof conversation?.text === 'string' && conversation.text.toLowerCase().includes(q)) return true
        if (typeof conversation?.conversationName === 'string' && conversation.conversationName.toLowerCase().includes(q)) {
            return true
        }
        return (
            conversation?.participants?.some((p: any) => {
                const fullName = `${p.firstName || ''} ${p.lastName || ''}`.trim().toLowerCase()
                return (
                    fullName.includes(q) ||
                    p.publicIdentifier?.toLowerCase().includes(q) ||
                    p.occupation?.toLowerCase().includes(q)
                )
            }) ?? false
        )
    }

    /** Label tabs (To respond, Awaiting reply, …) must scope LI search to conversations on that tab. */
    const inboxLabelRequiresScopedSearch = (label: string | undefined) =>
        !!label && label !== 'allMessages' && label !== 'mainInbox' && label !== 'salesNav'

    const mergeTagStatusOntoConversations = async (rows: any[]): Promise<any[]> => {
        const ids = rows.map((c: any) => c.conversationId).filter(Boolean)
        if (!ids.length) return rows

        const res: any = await getConversationsTagStatus(ids).catch(() => null)
        const tagRows: any[] = res?.conversations ?? res?.data?.conversations ?? []
        const tagById = new Map(tagRows.map((r: any) => [r.conversationId, r]))

        return rows.map((row: any) => {
            const db = tagById.get(row.conversationId)
            if (!db) return row
            return {
                ...row,
                conversationTags: db.conversationTags ?? row.conversationTags,
                conversationTagPending: db.conversationTagPending ?? row.conversationTagPending,
            }
        })
    }

    const conversationTimestampValue = (value: any) => {
        if (value == null || value === '') return 0
        const numeric = typeof value === 'number' ? value : Number(value)
        if (Number.isFinite(numeric)) return numeric
        const parsed = Date.parse(value)
        return Number.isFinite(parsed) ? parsed : 0
    }

    const sortConversationsByRecency = (rows: any[]) =>
        [...rows].sort((a: any, b: any) => {
            if (a?.isPinned && !b?.isPinned) return -1
            if (!a?.isPinned && b?.isPinned) return 1
            return conversationTimestampValue(b?.dateTimestamp) - conversationTimestampValue(a?.dateTimestamp)
        })

    const mergeConversationLists = (...lists: any[][]) => {
        const byId = new Map<string, any>()
        for (const list of lists) {
            for (const row of list) {
                const id = row?.conversationId
                if (!id) continue
                const existing = byId.get(id)
                byId.set(id, existing ? { ...existing, ...row } : row)
            }
        }
        return sortConversationsByRecency(Array.from(byId.values()))
    }

    const mergeLinkedInRowsWithDbRows = (linkedInRows: any[], dbRows: any[]) => {
        const dbById = new Map(dbRows.map((row: any) => [row.conversationId, row]))
        return linkedInRows.map((row: any) => {
            const db = dbById.get(row.conversationId)
            if (!db) return row
            return {
                ...db,
                ...row,
                tags: row.tags ?? db.tags,
                conversationTags: db.conversationTags ?? row.conversationTags,
                conversationTagPending: db.conversationTagPending ?? row.conversationTagPending,
                isSales: db.isSales ?? row.isSales,
                participants: row.participants?.length ? row.participants : db.participants,
            }
        })
    }

    /** Load full DB rows for LinkedIn/SN search hits (tags, AI labels, pin state, …). */
    const fetchDbRowsForConversationIds = async (
        ids: string[],
        isSalesOverride?: boolean | undefined,
    ): Promise<any[]> => {
        const idSet = new Set(ids.filter(Boolean))
        if (!idSet.size) return []

        const label = latestInboxStoreRef.current.activeInboxLabel
        const filters = getFilterParams(label)
        const paginateOpts = Object.keys(filters).length > 0 ? { ...filters } : undefined
        const found: any[] = []
        const foundIds = new Set<string>()

        for (let page = 1; page <= 8; page++) {
            const result: any = await paginateConversations({
                page,
                filters: paginateOpts,
                ...(isSalesOverride !== undefined ? { isSales: isSalesOverride } : {}),
            })
            const batch: any[] = result?.conversations ?? []
            for (const row of batch) {
                if (idSet.has(row.conversationId) && !foundIds.has(row.conversationId)) {
                    found.push(row)
                    foundIds.add(row.conversationId)
                }
            }
            if (foundIds.size >= idSet.size || result?.metadata?.hasNext === false || !batch.length) {
                break
            }
        }
        return found
    }

    const enrichSalesNavigatorSearchResults = async (rows: any[]): Promise<any[]> => {
        if (!rows.length) return []
        const flagged = rows.map((row: any) => ({ ...row, isSales: true }))
        const dbRows = await fetchDbRowsForConversationIds(
            flagged.map((c: any) => c.conversationId),
            true,
        )
        const merged = mergeLinkedInRowsWithDbRows(flagged, dbRows)
        const tagEnriched = await mergeTagStatusOntoConversations(merged)
        return tagEnriched.filter((c: any) => c.isSales === true)
    }

    const enrichRegularSearchResultsFromDb = async (rows: any[]): Promise<any[]> => {
        const regularOnly = rows.filter((c: any) => !c.isSales)
        if (!regularOnly.length) return []
        const dbRows = await fetchDbRowsForConversationIds(
            regularOnly.map((c: any) => c.conversationId),
            false,
        )
        const merged = mergeLinkedInRowsWithDbRows(regularOnly, dbRows)
        return mergeTagStatusOntoConversations(merged)
    }

    const fetchRegularLinkedInSearchPage = async (nextCursor: string) => {
        const queryParamsRes: any = await getInboxQueryParams().catch(() => null)
        const voyagerMessagingGraphQL: any = queryParamsRes?.data ?? queryParamsRes
        if (!voyagerMessagingGraphQL?.queryId || !voyagerMessagingGraphQL?.profileId) {
            return { rows: [] as any[], nextCursor: null as string | null }
        }

        const syncRes: any = await syncConversations({
            voyagerMessagingGraphQL,
            filter: { archived: archived === 'Yes' ? 'Yes' : 'No', read: readed },
            query: query.trim(),
            nextCursor,
        }).catch(() => false as any)

        if (syncRes === false || syncRes == null) {
            return { rows: [] as any[], nextCursor: null as string | null }
        }

        const rows = extractConversationsFromExtensionSync(syncRes)
        return { rows, nextCursor: extractNextCursor(syncRes) }
    }

    /** Paginate under label filters until LI hit ids are found (snoozed needs `snoozedUntil` from DB). */
    const fetchLabelTabRowsForLinkedInIds = async (
        liIds: Set<string>,
        label: string,
        maxPages = 8,
    ): Promise<any[]> => {
        const filters = getFilterParams(label)
        const paginateOpts = Object.keys(filters).length > 0 ? { ...filters } : undefined
        const found: any[] = []
        const foundIds = new Set<string>()

        for (let page = 1; page <= maxPages; page++) {
            const result: any = await paginateInboxListPage({ page, filters: paginateOpts })
            const batch: any[] = result?.conversations ?? []
            for (const row of batch) {
                if (liIds.has(row.conversationId) && !foundIds.has(row.conversationId)) {
                    found.push(row)
                    foundIds.add(row.conversationId)
                }
            }
            if (foundIds.size >= liIds.size || result?.metadata?.hasNext === false || !batch.length) {
                break
            }
        }
        return found
    }

    const scopeLinkedInSearchToActiveLabel = async (
        linkedInRows: any[],
        label: string | undefined,
    ): Promise<any[]> => {
        if (!inboxLabelRequiresScopedSearch(label)) {
            return linkedInRows
        }

        if (label === 'snoozed' || label === 'newConnection') {
            const liIds = new Set(linkedInRows.map((c: any) => c.conversationId).filter(Boolean))
            const dbRows = await fetchLabelTabRowsForLinkedInIds(liIds, label)
            const dbById = new Map(dbRows.map((r: any) => [r.conversationId, r]))
            return linkedInRows
                .filter((li: any) => dbById.has(li.conversationId))
                .map((li: any) => ({ ...li, ...dbById.get(li.conversationId) }))
                .filter(
                    (c: any) =>
                        !isDismissedFromLabelTab(c.conversationId, label) &&
                        conversationMatchesInboxLabel(c, label),
                )
        }

        const merged = await mergeTagStatusOntoConversations(linkedInRows)
        return merged.filter(
            (c: any) =>
                !isDismissedFromLabelTab(c.conversationId, label) &&
                conversationMatchesInboxLabel(c, label),
        )
    }

    /**
     * LinkedIn search: extension merges LI + DB via `synchronizeConversations`.
     * - allMessages / mainInbox: use LI rows as-is (no paginate page-1 intersect).
     * - label tabs: enrich tags from DB, keep only rows that belong on the active tab.
     * - LI empty: paginate + client-side name/text match.
     */
    const hydrateInboxSearchResultsFromDb = async (linkedInRows: any[]): Promise<any[]> => {
        const label = latestInboxStoreRef.current.activeInboxLabel

        if (linkedInRows.length > 0) {
            return scopeLinkedInSearchToActiveLabel(linkedInRows, label)
        }

        const filters = getFilterParams(label)
        const paginateOpts = Object.keys(filters).length > 0 ? { ...filters } : undefined
        const result: any = await paginateInboxListPage({ page: 1, filters: paginateOpts })
        const dbRows: any[] = result?.conversations ?? []
        return dbRows.filter((c: any) => conversationMatchesSearchQuery(c, query))
    }

    /** Keep selection behavior in sync with `getConversationsFirstPage` / linkedinextension `filterConversations`. */
    const autoSelectFirstConversationAfterListLoad = (convList: any[]) => {
        if (!convList.length) {
            if (selectedConversation) {
                inboxDispatch({ type: 'reset-selected-conversation' })
                inboxDispatch({ type: 'set-selected-profile', payload: null })
                inboxDispatch({ type: 'set-selected-connection', payload: null })
            }
            return
        }
        if (readed === 'All Unread') {
            inboxDispatch({ type: 'reset-selected-conversation' })
            inboxDispatch({ type: 'set-selected-profile', payload: null })
            inboxDispatch({ type: 'set-selected-connection', payload: null })
            return
        }
        const currentId = selectedConversation?.conversationId
        const stillInList =
            !!currentId && convList.some((c: any) => c.conversationId === currentId)
        if (!stillInList) {
            void openConversation(convList[0])
        }
    }

    /**
     * Filtered inbox list — parity with linkedinextension `getFilteredLDConversations`.
     * Text search hits LinkedIn/SN APIs (V1 / inboxGetConversations), not debounced local filtering.
     */
    const getFilteredLDConversations = async (
        getConversationsFromLinkedin: boolean,
        { silent }: { silent?: boolean } = {},
    ) => {
        linkedinInboxDrainGenerationRef.current += 1
        inboxDispatch({ type: 'reset-all-conversations-fetched' })
        if (!silent) inboxDispatch({ type: 'start-fetching-conversations' })

        try {
            await loadSalesNavigatorUser()
            if (isSalesNavigatorTab && !guardSalesNavigatorAction()) {
                if (!silent) inboxDispatch({ type: 'end-fetching-conversations' })
                return
            }

            let data: any[] = []

            if (salesNavigatorSearchActive) {
                const raw = await syncSalesNavigatorConversations({
                    query: query.trim(),
                    filter: { archived, read: readed },
                })
                data = await enrichSalesNavigatorSearchResults(extractConversationsFromExtensionSync(raw))
                const snNextCursor = extractNextCursor(raw)
                inboxDispatch({ type: 'set-sales-navigator-next-cursor', payload: snNextCursor })
                if (data.length) {
                    const last = data[data.length - 1]
                    inboxDispatch({ type: 'set-last-fetched-conversation', payload: last })
                }
                if (!snNextCursor) {
                    inboxDispatch({ type: 'set-all-conversations-fetched' })
                }
            } else if (allMessagesSearchActive) {
                let regularRows: any[] = []
                let regularNextCursor: string | null = null

                const regularPage = await fetchRegularLinkedInSearchPage('')
                regularRows = regularPage.rows
                regularNextCursor = regularPage.nextCursor
                inboxDispatch({ type: 'set-linkedin-next-cursor', payload: regularNextCursor })

                if (regularRows.length) {
                    await persistFilteredConversationsToDb(regularRows)
                    inboxDispatch({ type: 'set-last-fetched-conversation', payload: regularRows[regularRows.length - 1] })
                }

                regularRows = await enrichRegularSearchResultsFromDb(regularRows)

                let salesRows: any[] = []
                let salesNextCursor: string | null = null
                if (canSyncSalesNavigatorExtension()) {
                    const snRaw = await syncSalesNavigatorConversations({
                        query: query.trim(),
                        filter: { archived, read: readed },
                    })
                    salesRows = await enrichSalesNavigatorSearchResults(extractConversationsFromExtensionSync(snRaw))
                    salesNextCursor = extractNextCursor(snRaw)
                }
                inboxDispatch({ type: 'set-sales-navigator-next-cursor', payload: salesNextCursor })

                data = mergeConversationLists(regularRows, salesRows)
                if (!regularNextCursor && !salesNextCursor) {
                    inboxDispatch({ type: 'set-all-conversations-fetched' })
                }
            } else if (getConversationsFromLinkedin || query.trim().length > 0) {
                /**
                 * Text search always via LinkedIn V1 (`keywords:` / messengerConversationsBySearchCriteria).
                 * REST `/inbox/conversations/paginate` does not apply `filters.query` — label tabs then
                 * narrow results in `filterConversations` (e.g. To respond + "Rasic").
                 */
                let isSuccess = false
                let attempts = 0

                while (!isSuccess && attempts < 3) {
                    const queryParamsRes: any = await getInboxQueryParams().catch(() => null)
                    const voyagerMessagingGraphQL: any = queryParamsRes?.data ?? queryParamsRes
                    if (!voyagerMessagingGraphQL?.queryId || !voyagerMessagingGraphQL?.profileId) {
                        break
                    }

                    const syncRes: any = await syncConversations({
                        voyagerMessagingGraphQL,
                        filter: { archived: archived === 'Yes' ? 'Yes' : 'No', read: readed },
                        query: query.trim(),
                        nextCursor: '',
                    }).catch(() => false as any)

                    data = extractConversationsFromExtensionSync(syncRes)
                    const nextCursor = extractNextCursor(syncRes)
                    inboxDispatch({ type: 'set-linkedin-next-cursor', payload: nextCursor })

                    isSuccess = data.length === 0 || data.length > 0
                    attempts++

                    if (isSuccess && data.length) {
                        await persistFilteredConversationsToDb(data)
                        inboxDispatch({ type: 'set-last-fetched-conversation', payload: data[data.length - 1] })
                    }
                }

                if (attempts >= 3) {
                    const label = latestInboxStoreRef.current.activeInboxLabel
                    const filters = getFilterParams(label)
                    const hasFilters = Object.keys(filters).length > 0 || query.length > 0
                    const paginateOpts = hasFilters ? { ...filters, ...(query.trim() && { query: query.trim() }) } : undefined
                    const result: any = await paginateInboxListPage({ page: 1, filters: paginateOpts })
                    data = result?.conversations ?? []
                    setToast({ type: 'error', message: 'There was an error filtering your conversations.' })
                }
            } else {
                const label = latestInboxStoreRef.current.activeInboxLabel
                const filters = getFilterParams(label)
                const hasFilters = Object.keys(filters).length > 0 || query.length > 0
                const paginateOpts = hasFilters ? { ...filters, ...(query.trim() && { query: query.trim() }) } : undefined
                const result: any = await paginateInboxListPage({ page: 1, filters: paginateOpts })
                data = result?.conversations ?? []
                if (result?.metadata?.hasNext === false || !data.length) {
                    inboxDispatch({ type: 'set-all-conversations-fetched' })
                }
            }

            if (query.trim().length > 0 && !salesNavigatorSearchActive && !allMessagesSearchActive) {
                data = await hydrateInboxSearchResultsFromDb(data)
                if (!data.length) {
                    inboxDispatch({ type: 'set-all-conversations-fetched' })
                }
            }

            inboxDispatch({ type: 'set-pinned-conversations', payload: data.filter((c: any) => c.isPinned) })
            inboxDispatch({ type: 'set-conversations', payload: data })
            const displayList = filterConversations(data)
            autoSelectFirstConversationAfterListLoad(displayList)

            if (!silent) inboxDispatch({ type: 'end-fetching-conversations' })
            if (!data.length) inboxDispatch({ type: 'set-all-conversations-fetched' })
        } catch (e) {
            console.error('[Inbox] getFilteredLDConversations error', e)
            if (!silent) inboxDispatch({ type: 'end-fetching-conversations' })
            setToast({ type: 'error', message: 'There was an error filtering your conversations.' })
        }
    }

    /** Classic inbox filtered list — next page from LinkedIn V1 cursor (linkedinextension `loadMoreConversations`). */
    const loadMoreConversationsFromLinkedin = async (): Promise<boolean> => {
        if (
            loadMoreConversationsInFlightRef.current ||
            fetchingMoreConversations ||
            fetchingConversations ||
            allConversationsFetched
        ) {
            return false
        }

        if (!linkedinNextCursor) {
            inboxDispatch({ type: 'set-all-conversations-fetched' })
            return false
        }

        loadMoreConversationsInFlightRef.current = true
        inboxDispatch({ type: 'start-fetching-more-conversations' })
        try {
            const queryParamsRes: any = await getInboxQueryParams().catch(() => null)
            const voyagerMessagingGraphQL: any = queryParamsRes?.data ?? queryParamsRes
            if (!voyagerMessagingGraphQL?.queryId || !voyagerMessagingGraphQL?.profileId) {
                return false
            }

            const syncRes: any = await syncConversations({
                voyagerMessagingGraphQL,
                filter: { archived: archived === 'Yes' ? 'Yes' : 'No', read: readed },
                query: query.trim(),
                nextCursor: linkedinNextCursor,
            }).catch(() => false as any)

            const batch = extractConversationsFromExtensionSync(syncRes)
            const nextCursor = extractNextCursor(syncRes)
            inboxDispatch({ type: 'set-linkedin-next-cursor', payload: nextCursor })

            if (batch.length) {
                await persistFilteredConversationsToDb(batch)
                const deduped = batch.filter(
                    (nc: any) => !conversations.some((c: any) => c.conversationId === nc.conversationId),
                )
                if (deduped.length) {
                    inboxDispatch({ type: 'add-more-conversations', payload: deduped })
                }
                inboxDispatch({ type: 'set-last-fetched-conversation', payload: batch[batch.length - 1] })
            }

            if (!nextCursor) {
                inboxDispatch({ type: 'set-all-conversations-fetched' })
            }
            return batch.length > 0
        } catch (e) {
            console.error('[loadMoreConversationsFromLinkedin]', e)
            setToast({ type: 'error', message: 'There was an error loading more conversations.' })
            return false
        } finally {
            inboxDispatch({ type: 'end-fetching-more-conversations' })
            loadMoreConversationsInFlightRef.current = false
        }
    }

    const loadMoreSalesNavigatorSearchConversations = async (): Promise<boolean> => {
        if (
            loadMoreConversationsInFlightRef.current ||
            fetchingMoreConversations ||
            fetchingConversations ||
            !salesNavigatorSearchActive
        ) {
            return false
        }

        const snCursor = latestInboxStoreRef.current.salesNavigatorNextCursor ?? salesNavigatorNextCursor
        if (!snCursor) {
            inboxDispatch({ type: 'set-all-conversations-fetched' })
            return false
        }

        loadMoreConversationsInFlightRef.current = true
        inboxDispatch({ type: 'start-fetching-more-conversations' })
        try {
            const raw = await syncSalesNavigatorConversations({
                query: query.trim(),
                filter: { archived, read: readed },
                nextCursor: String(snCursor),
            })
            const nextCursor = extractNextCursor(raw)
            inboxDispatch({ type: 'set-sales-navigator-next-cursor', payload: nextCursor })

            const batch = await enrichSalesNavigatorSearchResults(extractConversationsFromExtensionSync(raw))
            if (!batch.length && !nextCursor) {
                inboxDispatch({ type: 'set-all-conversations-fetched' })
                return false
            }

            const currentList = latestInboxStoreRef.current.conversations ?? conversations
            const deduped = batch.filter(
                (nc: any) => !currentList.some((c: any) => c.conversationId === nc.conversationId),
            )
            if (deduped.length) {
                inboxDispatch({ type: 'add-more-conversations', payload: deduped })
            }

            if (batch.length) {
                inboxDispatch({ type: 'set-last-fetched-conversation', payload: batch[batch.length - 1] })
            }
            if (!nextCursor) {
                inboxDispatch({ type: 'set-all-conversations-fetched' })
            }
            return deduped.length > 0 || !!nextCursor
        } catch (e) {
            console.error('[loadMoreSalesNavigatorSearchConversations]', e)
            setToast({ type: 'error', message: 'There was an error loading more conversations.' })
            return false
        } finally {
            inboxDispatch({ type: 'end-fetching-more-conversations' })
            loadMoreConversationsInFlightRef.current = false
        }
    }

    const loadMoreAllMessagesSearchConversations = async (): Promise<boolean> => {
        if (
            loadMoreConversationsInFlightRef.current ||
            fetchingMoreConversations ||
            fetchingConversations ||
            allConversationsFetched ||
            !allMessagesSearchActive
        ) {
            return false
        }

        const regularCursor = latestInboxStoreRef.current.linkedinNextCursor ?? linkedinNextCursor
        const snCursor = latestInboxStoreRef.current.salesNavigatorNextCursor ?? salesNavigatorNextCursor
        if (!regularCursor && !snCursor) {
            inboxDispatch({ type: 'set-all-conversations-fetched' })
            return false
        }

        loadMoreConversationsInFlightRef.current = true
        inboxDispatch({ type: 'start-fetching-more-conversations' })
        try {
            let anyAdded = false
            const seenIds = new Set(
                (latestInboxStoreRef.current.conversations ?? conversations)
                    .map((c: any) => c.conversationId)
                    .filter(Boolean),
            )
            const appendUnique = (rows: any[]) => {
                const deduped = rows.filter((nc: any) => nc?.conversationId && !seenIds.has(nc.conversationId))
                if (deduped.length) {
                    deduped.forEach((nc: any) => seenIds.add(nc.conversationId))
                    inboxDispatch({ type: 'add-more-conversations', payload: deduped })
                    anyAdded = true
                }
            }

            let nextRegularCursor: string | null = regularCursor ?? null
            if (regularCursor) {
                const regularPage = await fetchRegularLinkedInSearchPage(regularCursor)
                nextRegularCursor = regularPage.nextCursor
                inboxDispatch({ type: 'set-linkedin-next-cursor', payload: nextRegularCursor })
                if (regularPage.rows.length) {
                    await persistFilteredConversationsToDb(regularPage.rows)
                    const enriched = await enrichRegularSearchResultsFromDb(regularPage.rows)
                    appendUnique(enriched)
                }
            }

            let nextSnCursor: string | null = snCursor ?? null
            if (snCursor && canSyncSalesNavigatorExtension()) {
                const snRaw = await syncSalesNavigatorConversations({
                    query: query.trim(),
                    filter: { archived, read: readed },
                    nextCursor: String(snCursor),
                })
                nextSnCursor = extractNextCursor(snRaw)
                inboxDispatch({ type: 'set-sales-navigator-next-cursor', payload: nextSnCursor })
                const enriched = await enrichSalesNavigatorSearchResults(extractConversationsFromExtensionSync(snRaw))
                appendUnique(enriched)
            }

            if (!nextRegularCursor && !nextSnCursor) {
                inboxDispatch({ type: 'set-all-conversations-fetched' })
            }
            return anyAdded || !!nextRegularCursor || !!nextSnCursor
        } catch (e) {
            console.error('[loadMoreAllMessagesSearchConversations]', e)
            setToast({ type: 'error', message: 'There was an error loading more conversations.' })
            return false
        } finally {
            inboxDispatch({ type: 'end-fetching-more-conversations' })
            loadMoreConversationsInFlightRef.current = false
        }
    }

    const getConversationsFirstPage = async (expectedInboxLabel?: string) => {
        const currentLabel = latestInboxStoreRef.current.activeInboxLabel
        if (expectedInboxLabel && currentLabel !== expectedInboxLabel) {
            return
        }
        inboxDispatch({ type: 'reset-all-conversations-fetched' })
        /** Same tick as reset so the list never briefly shows stale rows or an empty "No results" state before fetch. */
        inboxDispatch({ type: 'start-fetching-conversations' })
        linkedinInboxDrainGenerationRef.current += 1
        const inboxLoadSession = linkedinInboxDrainGenerationRef.current
        linkedinInboxDrainRunningRef.current = false
        getFilteringTagOptions()

        try {
            await loadSalesNavigatorUser()

            if (expectedInboxLabel && latestInboxStoreRef.current.activeInboxLabel !== expectedInboxLabel) {
                inboxDispatch({ type: 'end-fetching-conversations' })
                return
            }

            if (isSalesNavigatorTab && !guardSalesNavigatorAction()) {
                inboxDispatch({ type: 'end-fetching-conversations' })
                return
            }

            // Pass the label resolved from the ref so getFilterParams never uses a stale closure value.
            const filters = getFilterParams(latestInboxStoreRef.current.activeInboxLabel)
            const hasFilters = Object.keys(filters).length > 0 || query.length > 0
            const paginateOpts = hasFilters ? { ...filters, ...(query.trim() && { query: query.trim() }) } : undefined

            console.log('[Inbox] getConversationsFirstPage filters', {
                pinned,
                starred,
                readed,
                archived,
                tagsCount: tags.length,
                query,
                activeInboxLabel,
                lastMessagedFrom,
                lastMessagedTo,
                conversationStatus: conversationStatus?.value,
            })
            console.log('[Inbox] getConversationsFirstPage request', {
                page: 1,
                hasFilters,
                filters,
                paginateOpts,
                extensionCanSyncClassicInbox: extensionCanSyncClassicInbox(),
                extensionCanSyncSalesNavigator: extensionCanSyncSalesNavigator(),
            })

            // Always read list from LeadDelta API first — never block the UI waiting on the extension (can hang indefinitely).
            const restResult = await paginateInboxListPage({ page: 1, filters: paginateOpts })

            if (expectedInboxLabel && latestInboxStoreRef.current.activeInboxLabel !== expectedInboxLabel) {
                inboxDispatch({ type: 'end-fetching-conversations' })
                return
            }

            if (inboxLoadSession !== linkedinInboxDrainGenerationRef.current) {
                return
            }

            console.log('[Inbox] getConversationsFirstPage paginate response', restResult)

            // Treat `undefined` as "request was aborted/failed" – do NOT touch pagination state in that case.
            // Otherwise empty-array fallback below would mark the list as fully fetched on a transient cancel.
            if (restResult == null) {
                inboxDispatch({ type: 'end-fetching-conversations' })
                return
            }

            const conversationsData: any[] = restResult?.conversations ?? []
            // DB-side hasNext only describes what is currently in our DB. With the V1 sync
            // strategy, DB may temporarily have just one LinkedIn page; the AUTHORITATIVE
            // signal is `linkedinNextCursor` which the V1 sync sets a moment later. We will
            // only set `set-all-conversations-fetched` once we know LI has nothing more
            // (or when V1 sync is not applicable for the current filters).
            const hasNext = restResult?.metadata?.hasNext
            const willRunV1Sync = extensionCanSyncClassicInbox()
            const willRunSnSync = extensionCanSyncSalesNavigator()
            const willRunExtensionSync = willRunV1Sync || willRunSnSync
            const hasRealNarrowingFilter =
                readed !== 'All' ||
                pinned !== 'All' ||
                starred !== 'All' ||
                archived === 'Yes' ||
                tags.length > 0 ||
                !!lastMessagedFrom?.length ||
                !!lastMessagedTo?.length ||
                conversationStatus?.value !== 'all' ||
                lastMessagedOnlyLast ||
                hasConversationOwnerFilter() ||
                hasDbOnlyLabelFilter() ||
                query.length > 0
            /** DB is empty but extension will populate — keep list skeleton until first sync (or timeout) finishes.
             *  When a real narrowing filter is active the DB result is authoritative — don't wait for extension. */
            const awaitingExtensionInboxBootstrap =
                conversationsData.length === 0 && willRunExtensionSync && !hasRealNarrowingFilter
            const endFetchingIfSameSession = () => {
                if (inboxLoadSession !== linkedinInboxDrainGenerationRef.current) return
                inboxDispatch({ type: 'end-fetching-conversations' })
            }
            const pinnedData = conversationsData.filter((c: any) => c.isPinned)

            console.log('[Inbox] getConversationsFirstPage summary', {
                conversationsLength: conversationsData.length,
                pinnedRows: pinnedData.length,
                metadata: restResult?.metadata,
                hasNext,
                willRunV1Sync,
                willRunSnSync,
                firstFewIds: conversationsData.slice(0, 5).map((c: any) => ({
                    conversationId: c?.conversationId,
                    _id: c?._id,
                    preview: typeof c?.text === 'string' ? c.text.slice(0, 80) : c?.text,
                })),
                firstConversationSample: conversationsData[0] ?? null,
                awaitingExtensionInboxBootstrap,
            })

            inboxDispatch({ type: 'set-pinned-conversations', payload: pinnedData })
            inboxDispatch({ type: 'set-conversations', payload: conversationsData })
            if (!awaitingExtensionInboxBootstrap) {
                inboxDispatch({ type: 'end-fetching-conversations' })
            }
            if ((hasNext === false || conversationsData.length === 0) && (!willRunExtensionSync || hasRealNarrowingFilter)) {
                // DB-only combinations, or real narrowing filters (where DB is authoritative),
                // can trust the first REST page as final without waiting for extension sync.
                inboxDispatch({ type: 'set-all-conversations-fetched' })
            }
            autoSelectFirstConversationAfterListLoad(conversationsData)

            if (willRunExtensionSync && !inboxFullBackgroundSyncStartedRef.current) {
                inboxFullBackgroundSyncStartedRef.current = true
                backgroundSyncSessionRef.current += 1
                const syncSession = backgroundSyncSessionRef.current

                let bootstrapFinished = false
                const finishBootstrapOnce = () => {
                    if (bootstrapFinished) return
                    bootstrapFinished = true
                    endFetchingIfSameSession()
                }

                const syncFilter = { archived: archived === 'Yes' ? 'Yes' : 'No', read: readed }
                const drainPromise = runSequentialBackgroundSync(
                    syncSession,
                    paginateOpts,
                    syncFilter,
                    query,
                    inboxLoadSession,
                    awaitingExtensionInboxBootstrap ? finishBootstrapOnce : undefined,
                ).finally(() => {
                    if (awaitingExtensionInboxBootstrap) finishBootstrapOnce()
                })

                void Promise.race([
                    drainPromise,
                    new Promise<void>(resolve =>
                        setTimeout(() => {
                            console.warn(
                                `[Inbox] sequential background sync still running after ${EXTENSION_INBOX_FETCH_MS}ms — proveri SW logove ekstenzije.`,
                            )
                            finishBootstrapOnce()
                            resolve()
                        }, EXTENSION_INBOX_FETCH_MS),
                    ),
                ]).catch((err: unknown) => {
                    console.warn('[Inbox] sequential background sync rejected', err)
                    finishBootstrapOnce()
                })
            }
        } catch (e) {
            console.error('[Inbox conversations] getConversationsFirstPage error', e)
            inboxDispatch({ type: 'end-fetching-conversations' })
            setToast({ type: 'error', message: 'There was an error fetching your conversations.' })
        }
    }

    /**
     * Load more conversations.
     *
     * The DB only contains conversations that have been synced from LinkedIn so far. With the V1
     * sync strategy that runs once on inbox open, the DB may have just the most recent ~20 items
     * even though LinkedIn has many more. When the user scrolls past the end of what the DB knows,
     * we therefore:
     *   1. Pull the next LinkedIn page (using the stored `linkedinNextCursor`) and let the
     *      extension's BG synchronizeConversations push the new items into our DB.
     *      Skipped while `runLinkedInInboxBackgroundDrain` is active (it already chains LI pages).
     *   2. Re-paginate the DB to actually fetch the items.
     *   3. Persist the new cursor (or `null` when LinkedIn confirms there are no more pages).
     *
     * If V1 sync isn't applicable for the current filter combination, we fall back to plain DB
     * pagination (legacy behaviour).
     *
     * @returns true if the caller should increment its page counter (loaded a non-empty page or API explicitly set hasNext true); false if skipped, failed, exhausted, or empty page without hasNext true.
     */
    const loadMoreConversations = async (page: number): Promise<boolean> => {
        if (loadMoreConversationsInFlightRef.current || fetchingMoreConversations || fetchingConversations || allConversationsFetched) {
            console.log('[Inbox] loadMoreConversations: skipped', {
                page,
                inFlight: loadMoreConversationsInFlightRef.current,
                fetchingMoreConversations,
                fetchingConversations,
                allConversationsFetched,
            })
            return false
        }

        if (isSalesNavigatorTab && !guardSalesNavigatorAction()) return false

        const currentLinkedinCursor = latestInboxStoreRef.current.linkedinNextCursor ?? linkedinNextCursor
        const currentSnCursor = latestInboxStoreRef.current.salesNavigatorNextCursor ?? salesNavigatorNextCursor
        const allMessagesBrowse = isAllMessagesTab && !query.trim()

        console.log('[Inbox] loadMoreConversations: requesting', {
            page,
            linkedinNextCursor: currentLinkedinCursor,
            salesNavigatorNextCursor: currentSnCursor,
            allMessagesBrowse,
        })
        loadMoreConversationsInFlightRef.current = true
        inboxDispatch({ type: 'start-fetching-more-conversations' })
        const loadMoreListSession = linkedinInboxDrainGenerationRef.current

        try {
            const filters = getFilterParams(latestInboxStoreRef.current.activeInboxLabel)
            const hasFilters = Object.keys(filters).length > 0 || query.length > 0
            const paginateOpts = hasFilters ? { ...filters, ...(query.trim() && { query: query.trim() }) } : undefined

            // ── Step 1: if extension sync is applicable and LI/SN still has more, fetch the next page first.
            // Skip while the full background drain is already pulling pages.
            const useV1Sync = extensionCanSyncClassicInbox()
            const useSnSync = extensionCanSyncSalesNavigator()
            let liExhausted = false
            let snExhausted = false

            if (!linkedinInboxDrainRunningRef.current) {
                if (useV1Sync && currentLinkedinCursor) {
                    try {
                        const queryParamsRes: any = await getInboxQueryParams()
                        const voyagerMessagingGraphQL: any = queryParamsRes?.data ?? queryParamsRes
                        if (voyagerMessagingGraphQL?.queryId && voyagerMessagingGraphQL?.profileId) {
                            const syncRes: any = await syncConversations({
                                voyagerMessagingGraphQL,
                                filter: { archived: archived === 'Yes' ? 'Yes' : 'No', read: readed },
                                query,
                                nextCursor: currentLinkedinCursor,
                            })

                            const newCursor = extractNextCursor(syncRes)
                            const liConversations: any[] = Array.isArray(syncRes?.conversations)
                                ? syncRes.conversations
                                : Array.isArray(syncRes)
                                ? syncRes
                                : []
                            console.log('[Inbox] loadMore V1 sync result', {
                                length: liConversations.length,
                                nextCursorParsed: newCursor,
                            })
                            inboxDispatch({ type: 'set-linkedin-next-cursor', payload: newCursor })
                            if (!newCursor) liExhausted = true
                        } else {
                            console.warn('[Inbox] loadMore: /inbox/queryParams nije vratio queryId/profileId — preskačem V1 sync.')
                        }
                    } catch (e) {
                        console.warn('[Inbox] loadMore V1 sync failed (continuing with DB-only pagination):', e)
                    }
                } else if (useV1Sync && currentLinkedinCursor === null) {
                    liExhausted = true
                }

                const shouldSyncSnPage =
                    useSnSync && currentSnCursor && (isSalesNavigatorTab || allMessagesBrowse)
                if (shouldSyncSnPage) {
                    try {
                        const syncRes: any = await syncSalesNavigatorConversations({
                            nextCursor: currentSnCursor,
                        })
                        const newCursor = extractNextCursor(syncRes)
                        const liConversations: any[] = Array.isArray(syncRes?.conversations)
                            ? syncRes.conversations
                            : Array.isArray(syncRes)
                            ? syncRes
                            : []
                        console.log('[Inbox] loadMore SN sync result', {
                            length: liConversations.length,
                            nextCursorParsed: newCursor,
                        })
                        inboxDispatch({ type: 'set-sales-navigator-next-cursor', payload: newCursor })
                        if (!newCursor) snExhausted = true
                    } catch (e) {
                        console.warn('[Inbox] loadMore SN sync failed (continuing with DB-only pagination):', e)
                    }
                } else if (useSnSync && (isSalesNavigatorTab || allMessagesBrowse) && currentSnCursor === null) {
                    snExhausted = true
                }
            }

            if (loadMoreListSession !== linkedinInboxDrainGenerationRef.current) {
                return false
            }

            // ── Step 2: paginate DB. With V1 sync above, the new page should now be queryable.
            const result = await paginateInboxListPage({ page, filters: paginateOpts })
            console.log(`[Inbox] loadMore page ${page} response`, result)

            if (loadMoreListSession !== linkedinInboxDrainGenerationRef.current) {
                return false
            }

            // `undefined` = request was aborted (slug-cancel) or network/server error.
            // Do NOT mark "all fetched" – that would freeze the list. Caller will simply not
            // bump its `page` counter (we return false), and the next scroll can retry.
            if (result == null) {
                console.warn(`[Inbox] loadMore page ${page} aborted/failed – not flipping allConversationsFetched.`)
                return false
            }

            const newConversations: any[] = result?.conversations ?? []
            const hasNext = result?.metadata?.hasNext
            const deduped = newConversations.filter(
                (nc: any) => !conversations.some((c: any) => c.conversationId === nc.conversationId)
            )

            const extensionExhausted = allMessagesBrowse
                ? liExhausted && snExhausted
                : isSalesNavigatorTab
                ? snExhausted
                : liExhausted

            console.log(`[Inbox] loadMore page ${page} summary`, {
                newConversationsLength: newConversations.length,
                dedupedLength: deduped.length,
                hasNext,
                dedupedIds: deduped.slice(0, 5).map((c: any) => c.conversationId),
                liExhausted,
                snExhausted,
                extensionExhausted,
                useV1Sync,
                useSnSync,
            })

            if (deduped.length) {
                inboxDispatch({ type: 'add-more-conversations', payload: deduped })
            }

            // Stop only when BOTH the DB says no next page AND LI confirmed it has no more
            // (i.e. cursor became null). When V1 sync is not applicable, fall back to DB-only
            // truth so legacy filter combinations still terminate.
            const dbExhausted = hasNext === false || newConversations.length === 0
            // A "real narrowing filter" means the user explicitly chose a subset (Unread,
            // Pinned, Tags, date ranges…). The default `archived: No` (isArchived:false)
            // is sent to the DB but is NOT a user-chosen narrowing filter — more LinkedIn
            // pages might exist that haven't been synced yet.
            // When a real narrowing filter is active, the DB result IS the complete subset
            // (extension syncs with the same read/archived flags), so DB is authoritative.
            const hasRealNarrowingFilter =
                readed !== 'All' ||
                pinned !== 'All' ||
                starred !== 'All' ||
                archived === 'Yes' ||
                tags.length > 0 ||
                !!lastMessagedFrom?.length ||
                !!lastMessagedTo?.length ||
                conversationStatus?.value !== 'all' ||
                lastMessagedOnlyLast ||
                hasConversationOwnerFilter() ||
                hasDbOnlyLabelFilter() ||
                query.length > 0
            const trustDbOnly = (!useV1Sync && !useSnSync) || hasRealNarrowingFilter

            if (dbExhausted && (trustDbOnly || extensionExhausted)) {
                inboxDispatch({ type: 'set-all-conversations-fetched' })
                // No more pages to request — stop caller from incrementing page
                return false
            }

            // Do not use `hasNext !== false`: when `hasNext` is missing and the page is empty, that
            // expression is true and ConversationsList bumps `page` without ever appending rows (scroll looks broken).
            if (hasNext === false) {
                inboxDispatch({ type: 'set-all-conversations-fetched' })
                return false
            }
            if (hasNext === true) return true
            return newConversations.length > 0
        } catch (e) {
            console.error('[Inbox conversations] loadMore error', e)
            return false
        } finally {
            inboxDispatch({ type: 'end-fetching-more-conversations' })
            loadMoreConversationsInFlightRef.current = false
        }
    }

    const openConversation = async (conversation: any) => {
        inboxDispatch({ type: 'set-selected-connection', payload: null })
        if (conversation.participants?.length === 1) {
            inboxDispatch({ type: 'set-selected-profile', payload: { ...conversation.participants[0] } })
        } else {
            inboxDispatch({ type: 'set-selected-profile', payload: null })
        }

        const openedConversation = {
            ...conversation,
            isRead: true,
        }

        inboxDispatch({ type: 'set-selected-conversation', payload: openedConversation })

        // Note: getConnectionHandler is intentionally NOT called here.
        // ConversationDetails.tsx fires it via useEffect when the sidebar is mounted.
        // When the sidebar is closed, sendMessage has a live-fetch fallback for entityUrn.
        // Calling it here AND in ConversationDetails produced two identical requests where
        // the first was always aborted by the second (same sendRequest slug).

        try {
            if (selectedTab !== 'SalesNavigator') {
                await updateConversations({
                    type: 'open-conversation',
                    conversations: [{
                        _id: conversation?._id,
                        isArchived: conversation?.isArchived,
                        isRead: true,
                        isSent: conversation?.isSent,
                        dateTimestamp: conversation?.dateTimestamp,
                        text: conversation?.text
                    }]
                })
            }
        } catch (e) { /* ignore */ }

        inboxDispatch({ type: 'update-conversation-to-read', payload: openedConversation })
    }

    const normalizeObjectId = (value: any): string | null => {
        if (value == null) return null
        if (typeof value === 'string' && value.length) return value
        if (typeof value === 'object' && typeof value.$oid === 'string') return value.$oid
        if (typeof value === 'object' && value._id != null) return normalizeObjectId(value._id)
        return null
    }

    /**
     * Resolves the CRM connection for the inbox sidebar: vanity URL, linked connection id,
     * or participant profile document id (when LI vanity is missing — "Unknown" conversations).
     */
    const getConnectionHandler = async (conversationOrPublicId: any) => {
        if (typeof conversationOrPublicId === 'string') {
            if (!conversationOrPublicId) {
                inboxDispatch({ type: 'set-selected-connection', payload: null })
                return
            }
            try {
                const connection = await getConnectionByPublicIdentifier(conversationOrPublicId)
                inboxDispatch({ type: 'set-selected-connection', payload: connection ?? null })
            } catch {
                inboxDispatch({ type: 'set-selected-connection', payload: null })
            }
            return
        }

        const conversation = conversationOrPublicId
        if (!conversation?.participants || conversation.participants.length !== 1) {
            inboxDispatch({ type: 'set-selected-connection', payload: null })
            return
        }

        const p0 = conversation.participants[0]
        const publicIdentifier = p0?.publicIdentifier
        const memberId = p0?.memberId
        const profileDocId = normalizeObjectId(p0?._id ?? p0)
        const linkedConnectionId = normalizeObjectId(conversation.connections?.[0])

        const trySet = (conn: any) => {
            if (conn) {
                inboxDispatch({ type: 'set-selected-connection', payload: conn })
                return true
            }
            return false
        }

        try {
            if (publicIdentifier) {
                const c = await getConnectionByPublicIdentifier(publicIdentifier)
                if (trySet(c)) return
            }
            if (memberId) {
                try {
                    const c = await getConnectionByPublicIdentifier(memberId)
                    if (trySet(c)) return
                } catch { /* not a public identifier */ }
            }
            if (linkedConnectionId) {
                try {
                    const c = await getConnectionByProfileId(linkedConnectionId as any)
                    if (trySet(c)) return
                } catch { /* ignore */ }
            }
            if (profileDocId) {
                try {
                    const c = await getConnectionByProfileDocumentId(profileDocId)
                    if (trySet(c)) return
                } catch { /* ignore */ }
            }
            inboxDispatch({ type: 'set-selected-connection', payload: null })
        } catch {
            inboxDispatch({ type: 'set-selected-connection', payload: null })
        }
    }

    // ===== MESSAGES =====
    const getMessagesFirstPage = async (messagesListRef: any) => {
        if (!selectedConversation) return
        if (!guardSalesNavigatorAction()) return
        inboxDispatch({ type: 'start-fetching-messages' })

        const useSalesApi = shouldUseSalesMessagingApi(selectedConversation)

        try {
            const data: any[] = await getMessagesFromConversation(
                selectedConversation.conversationId,
                undefined,
                useSalesApi,
            ) as any

            if (data?.length) {
                if (data.length >= 20) {
                    inboxDispatch({ type: 'set-messages', payload: data.map((x: any) => ({ ...x, isDelivered: true })) })
                    if (!useSalesApi) {
                        const lastMsg = data[data.length - 1]
                        // data is oldest-first, newest-last → last 3 are most recent in chronological order
                        const recentMessages = data.slice(-3)
                            .filter((m: any) => typeof (m?.text ?? '') === 'string' && (m?.text ?? '').trim())
                            .map((m: any) => ({ sender: m.isSent ? 'user' : 'peer', text: (m.text ?? '').trim() }))
                        // The newest fetched message differs from the stored preview when a message was
                        // sent/received outside the app (e.g. directly on LinkedIn). In that case the
                        // backend re-labels the conversation on this update.
                        const newText = lastMsg?.text
                        const textChanged =
                            typeof newText === 'string' && newText.trim().length > 0 &&
                            newText !== selectedConversation?.text
                        await updateConversations({
                            type: 'messages-first-page',
                            conversations: [{ _id: selectedConversation?._id, isRead: true, dateTimestamp: lastMsg?.dateTimestamp, text: lastMsg?.text, recentMessages }]
                        })
                        // Mark the conversation pending locally so the tag-polling hook fetches the new
                        // label without requiring a full page refresh (mirrors the send-message flow).
                        if (textChanged && selectedConversation?.conversationId) {
                            const convId = selectedConversation.conversationId
                            markConversationTagRelabelPending(convId)
                            // Also reflect the newest message in the list preview immediately. The tag
                            // polling only refreshes the label, and 'update-conversation' alone does not
                            // touch filteredConversations (the rendered list), so we rebuild it here.
                            const previewPatch = {
                                conversationId: convId,
                                text: lastMsg?.text,
                                dateTimestamp: lastMsg?.dateTimestamp,
                                isSent: lastMsg?.isSent,
                            }
                            inboxDispatch({ type: 'update-conversation', payload: previewPatch })
                            const updatedConversations = conversations.map((conv: any) =>
                                conv.conversationId === convId
                                    ? { ...conv, ...previewPatch, conversationTagPending: true }
                                    : conv
                            )
                            filterConversations(updatedConversations)
                        }
                    }
                } else {
                    inboxDispatch({ type: 'add-last-batch-of-messages', payload: data })
                }
            } else {
                inboxDispatch({ type: 'set-all-messages-fetched' })
            }
        } catch (e) {
            console.error(e)
            inboxDispatch({ type: 'set-all-messages-fetched' })
        } finally {
            inboxDispatch({ type: 'end-fetching-messages' })
        }
    }

    const loadMoreMessages = async ({ target }: any) => {
        if (fetchingMessages || fetchingMoreMessages || allMessagesFetched || target.scrollTop > 5) return
        if (!guardSalesNavigatorAction()) return
        inboxDispatch({ type: 'start-fetching-more-messages' })

        const useSalesApi = shouldUseSalesMessagingApi(selectedConversation)

        try {
            const createdBefore = messages?.[0]?.dateTimestamp
            const data: any = await getMessagesFromConversation(
                selectedConversation?.conversationId,
                createdBefore,
                useSalesApi,
            )

            if (data?.length) {
                data.length >= 20
                    ? inboxDispatch({ type: 'add-more-messages', payload: data })
                    : inboxDispatch({ type: 'add-last-batch-of-messages', payload: data })
            } else {
                inboxDispatch({ type: 'set-all-messages-fetched' })
            }
        } catch (e) {
            console.error(e)
        } finally {
            inboxDispatch({ type: 'end-fetching-more-messages' })
        }
    }

    const sendMessage = async (_id: string, conversationId: string, text: string, attachmentsList: any[], connectionId?: string) => {
        try {
            if (!guardSalesNavigatorAction()) return false

            const linkedInUser: any = await getLoggedInLIUserForInbox()

            if (linkedInUser?.entityUrn !== user?.linkedinData?.entityUrn) {
                setToast({ type: 'info', message: 'You are using different LinkedIn account from the one that is synchronized with LeadDelta.' })
                return false
            }

            const fileToDataUrl = (file: File) => new Promise<string>((resolve, reject) => {
                const reader = new FileReader()
                reader.onload = () => resolve(String(reader.result || ''))
                reader.onerror = () => reject(reader.error)
                reader.readAsDataURL(file)
            })

            const toComposerAttachment = async (att: any) => {
                // Already in expected shape (new inbox stores objects)
                if (att && typeof att === 'object' && att.reference && att.name && att.byteSize != null) {
                    if (typeof att.reference === 'string' && att.reference.startsWith('data:')) return att
                    // Convert File -> data URL for extension upload
                    if (att.file instanceof File) {
                        return { ...att, reference: await fileToDataUrl(att.file) }
                    }
                    return att
                }
                // Old shape: File
                if (att instanceof File) {
                    return {
                        file: att,
                        name: att.name,
                        byteSize: att.size,
                        mediaType: att.type,
                        reference: await fileToDataUrl(att),
                    }
                }
                return att
            }

            const extAttachments = attachmentsList?.length
                ? (await Promise.all(attachmentsList.map(toComposerAttachment)))
                    .filter(Boolean)
                    .map((a: any) => ({
                        name: a.name,
                        byteSize: a.byteSize,
                        mediaType: a.mediaType,
                        // MUST be fetchable in extension BG context → data URL
                        reference: a.reference,
                    }))
                : []

            const optimisticAttachments = attachmentsList?.length
                ? attachmentsList
                    .filter(Boolean)
                    .map((a: any) => {
                        if (a && typeof a === 'object' && a.name && a.reference) return a
                        if (a instanceof File) {
                            return {
                                name: a.name,
                                byteSize: a.size,
                                mediaType: a.type,
                                reference: URL.createObjectURL(a),
                            }
                        }
                        return a
                    })
                : []

            const newMessage = {
                text,
                attachments: optimisticAttachments,
                dateTimestamp: moment().valueOf(),
                isSent: true,
                sender: {
                    firstName: linkedInUser.firstName,
                    lastName: linkedInUser.lastName,
                    profilePicture: linkedInUser.profilePicture
                },
                isDelivered: false,
                tempId: uuidv4()
            }

            // If the user switched conversations while waiting for extension APIs,
            // don't leak the optimistic message into the newly-selected thread.
            const stillViewingSameConversation =
                latestInboxStoreRef.current.selectedConversation?.conversationId === conversationId

            if (stillViewingSameConversation) {
                inboxDispatch({ type: 'add-sent-message', payload: newMessage })
            }

            let participantEntityUrn: string | null = selectedConnection?.entityUrn
                ?? selectedConversation?.participants?.[0]?.entityUrn
                ?? null

            // Fallback: fetch connection live if still no entityUrn (selectedConnection not yet loaded)
            if (!participantEntityUrn && selectedConversation?.participants?.length === 1) {
                const publicIdentifier = selectedConversation.participants[0]?.publicIdentifier
                if (publicIdentifier) {
                    try {
                        const conn = await getConnectionByPublicIdentifier(publicIdentifier)
                        participantEntityUrn = conn?.entityUrn ?? null
                        if (conn) inboxDispatch({ type: 'set-selected-connection', payload: conn })
                    } catch {}
                }
            }

            const useSalesApi = shouldUseSalesMessagingApi(selectedConversation)
            const data: any = useSalesApi
                ? await sendMessageInConversation(conversationId, text, true)
                : await createOrSendMessage({
                      conversationId,
                      profileId: user?.linkedinData?.entityUrn,
                      participantUrns: participantEntityUrn ? [participantEntityUrn] : [],
                      text,
                      ...(extAttachments.length ? { attachments: extAttachments } : {}),
                      isSales: false,
                  })
            const messageId = data?.messageId || data?.result?.messageId

            if (messageId) {
                if (stillViewingSameConversation) {
                    inboxDispatch({ type: 'update-message', payload: { ...newMessage, messageId, isDelivered: true } })
                }
                // messages in closure = store state before this render's dispatches (oldest-first, newest-last)
                // take last 2 existing + the new sent message to build 3-message recent context
                const prevMsgs = (messages || []).slice(-2)
                const sentMsg = { text, isSent: true, dateTimestamp: moment().unix() * 1000 }
                const recentMessages = [...prevMsgs, sentMsg]
                    .filter((m: any) => typeof (m?.text ?? '') === 'string' && (m?.text ?? '').trim())
                    .map((m: any) => ({ sender: m.isSent ? 'user' : 'peer', text: (m.text ?? '').trim() }))
                await updateConversations({
                    type: 'send-message',
                    conversations: [{
                        _id,
                        text,
                        dateTimestamp: moment().unix() * 1000,
                        isSent: true,
                        recentMessages,
                    }]
                })
                if (selectedConversation?.snoozedUntil && selectedConversation.snoozedUntil > Date.now()) {
                    inboxDispatch({
                        type: 'update-conversation',
                        payload: { conversationId, snoozedUntil: null, showSnoozedReturnedLabel: false },
                    })
                }
                markConversationTagRelabelPending(conversationId)
                return true
            } else {
                if (stillViewingSameConversation) {
                    inboxDispatch({ type: 'update-message', payload: { ...newMessage, isFailed: true, isDelivered: false } })
                }
                return false
            }
        } catch (e) {
            console.error(e)
            setToast({ type: 'error', message: 'There was a problem messaging your connections.' })
            return false
        }
    }

    const resendMessage = async (message: any) => {
        if (message.isFailed) {
            inboxDispatch({ type: 'delete-message', payload: message })
            inboxDispatch({ type: 'set-message-attachments', payload: [] })
            sendMessage(selectedConversation?._id, selectedConversation?.conversationId, message?.text, messageAttachments, selectedConversation?.connectionId)
        }
    }

    const createNewConversationHandler = async (participantsConnections: any[], text: string, attachmentsList: any[]) => {
        setSpinner(true)
        try {
            if (!guardSalesNavigatorAction()) return false

            const linkedInUser: any = await getLoggedInLIUserForInbox()

            if (linkedInUser?.entityUrn !== user?.linkedinData?.entityUrn) {
                setToast({ type: 'info', message: 'You are using different LinkedIn account from the one that is synchronized with LeadDelta.' })
                return false
            }

            const participantUrns = participantsConnections.map((c: any) => `urn:li:fsd_profile:${c.profileId}`)
            const result: any = await createOrSendMessage({
                conversationId: null,
                profileId: user?.linkedinData?.entityUrn,
                participantUrns,
                text,
                ...(attachmentsList?.length ? { attachments: (await Promise.all(attachmentsList.map(async (a: any) => {
                    if (a && typeof a === 'object' && a.reference?.startsWith('data:')) return a
                    if (a?.file instanceof File) {
                        const reader = new FileReader()
                        const dataUrl = await new Promise<string>((resolve, reject) => {
                            reader.onload = () => resolve(String(reader.result || ''))
                            reader.onerror = () => reject(reader.error)
                            reader.readAsDataURL(a.file)
                        })
                        return { name: a.name, byteSize: a.byteSize, mediaType: a.mediaType, reference: dataUrl }
                    }
                    if (a instanceof File) {
                        const reader = new FileReader()
                        const dataUrl = await new Promise<string>((resolve, reject) => {
                            reader.onload = () => resolve(String(reader.result || ''))
                            reader.onerror = () => reject(reader.error)
                            reader.readAsDataURL(a)
                        })
                        return { name: a.name, byteSize: a.size, mediaType: a.type, reference: dataUrl }
                    }
                    return a
                }))).filter(Boolean) } : {}),
                isSales: isSalesNavigatorActive,
            })

            resetFilters()
            await getConversationsFirstPage()
            return true
        } catch (e) {
            console.error(e)
            setToast({ type: 'error', message: 'There was a problem messaging your connections.' })
            return false
        } finally {
            setSpinner(false)
        }
    }

    // ===== STAR / PIN =====
    const starConversationHandler = async ({ conversationId, isStarred }: any) => {
        setSpinner(true)
        try {
            await starConversationRequest(conversationId, isStarred)
            const viewerProfileId = user?.linkedinData?.entityUrn
            if (viewerProfileId) {
                inboxSyncLinkedinConversationStar([conversationId], isStarred, viewerProfileId).catch(() => {})
            }
            inboxDispatch({ type: 'set-conversations', payload: conversations.map((c: any) => c.conversationId === conversationId ? { ...c, isStarred } : c) })
            if (selectedConversation?.conversationId === conversationId) {
                inboxDispatch({ type: 'update-selected-conversation', payload: { ...selectedConversation, isStarred } })
            }
            setToast({ type: 'success', message: `Conversation successfully ${isStarred ? 'starred' : 'unstarred'}.` })
        } catch {
            setToast({ type: 'error', message: 'There was an error starring your conversation.' })
        } finally {
            setSpinner(false)
        }
    }

    const starConversationsHandler = async (isStarred: boolean) => {
        setSpinner(true)
        const conversationIds = selectedConversations.map((c: any) => c.conversationId)
        try {
            await starConversationsRequest(conversationIds, isStarred)
            const viewerProfileId = user?.linkedinData?.entityUrn
            if (viewerProfileId && conversationIds.length) {
                inboxSyncLinkedinConversationStar(conversationIds, isStarred, viewerProfileId).catch(() => {})
            }
            inboxDispatch({ type: 'set-conversations', payload: conversations.map((c: any) => conversationIds.includes(c.conversationId) ? { ...c, isStarred, isSelected: false } : c) })
            inboxDispatch({ type: 'set-selected-conversations', payload: [] })
            setToast({ type: 'success', message: `Conversations successfully ${isStarred ? 'starred' : 'unstarred'}.` })
        } catch {
            setToast({ type: 'error', message: 'There was an error starring your conversations.' })
        } finally {
            setSpinner(false)
        }
    }

    const pinConversationHandler = async ({ conversationId, isPinned }: any) => {
        setSpinner(true)
        try {
            if (pinnedConversationsArray.length >= 10 && isPinned) {
                setToast({ type: 'error', message: 'You can not pin any more conversations at the moment.' })
                return
            }
            await pinConversationRequest(conversationId, isPinned)
            const mapped = conversations.map((c: any) => c.conversationId === conversationId ? { ...c, isPinned } : c)
            inboxDispatch({ type: 'set-pinned-conversations', payload: mapped.filter((c: any) => c.isPinned) })
            inboxDispatch({ type: 'set-conversations', payload: [...mapped.filter((c: any) => c.isPinned), ...mapped.filter((c: any) => !c.isPinned)] })
            if (selectedConversation?.conversationId === conversationId) {
                inboxDispatch({ type: 'update-selected-conversation', payload: { ...selectedConversation, isPinned } })
            }
            setToast({ type: 'success', message: `Conversation successfully ${isPinned ? 'pinned' : 'unpinned'}.` })
        } catch {
            setToast({ type: 'error', message: 'There was an error pinning your conversation.' })
        } finally {
            setSpinner(false)
        }
    }

    const pinConversationsHandler = async (isPinned: boolean) => {
        setSpinner(true)
        let conversationIds = selectedConversations.map((c: any) => c.conversationId)
        try {
            if (pinnedConversationsArray.length >= 10 && isPinned) {
                setToast({ type: 'error', message: 'You can not pin any more conversations at the moment.' })
                return
            }
            await pinConversationsRequest(conversationIds, isPinned)
            const mapped = conversations.map((c: any) => conversationIds.includes(c.conversationId) ? { ...c, isPinned, isSelected: false } : c)
            inboxDispatch({ type: 'set-pinned-conversations', payload: mapped.filter((c: any) => c.isPinned) })
            inboxDispatch({ type: 'set-conversations', payload: [...mapped.filter((c: any) => c.isPinned), ...mapped.filter((c: any) => !c.isPinned)] })
            inboxDispatch({ type: 'set-selected-conversations', payload: [] })
            setToast({ type: 'success', message: `Conversations successfully ${isPinned ? 'pinned' : 'unpinned'}.` })
        } catch {
            setToast({ type: 'error', message: 'There was an error pinning your conversations.' })
        } finally {
            setSpinner(false)
        }
    }

    // ===== SNOOZE =====
    const snoozeConversationHandler = async (conversationId: string, snoozedUntil: number | null) => {
        const actionStartedLabel = latestInboxStoreRef.current.activeInboxLabel
        try {
            await snoozeConversationsRequest([conversationId], snoozedUntil)
            setToast({ type: 'success', message: snoozedUntil ? 'Conversation snoozed.' : 'Snooze removed.' })
            await getConversationsFirstPage(actionStartedLabel)
        } catch {
            setToast({ type: 'error', message: 'There was an error snoozing the conversation.' })
        }
    }

    const snoozeConversationsHandler = async (snoozedUntil: number | null) => {
        const actionStartedLabel = latestInboxStoreRef.current.activeInboxLabel
        const conversationIds = selectedConversations.map((c: any) => c.conversationId)
        try {
            await snoozeConversationsRequest(conversationIds, snoozedUntil)
            inboxDispatch({ type: 'set-selected-conversations', payload: [] })
            setToast({
                type: 'success',
                message: snoozedUntil
                    ? `Snoozed ${conversationIds.length} until ${moment(snoozedUntil).format('ddd, h:mm A')}`
                    : `Unsnoozed ${conversationIds.length} conversations`,
            })
            await getConversationsFirstPage(actionStartedLabel)
        } catch {
            setToast({ type: 'error', message: 'There was an error snoozing your conversations.' })
        }
    }

    // ===== READ =====
    const readConversationHandler = async (conversation: any, isRead: boolean) => {
        try {
            await updateConversations({ type: 'read-conversation', conversations: [{ _id: conversation?._id, isRead }] })
            inboxMarkAsSeen(conversation?.conversationId, isRead).catch(() => {})
            inboxDispatch({ type: 'update-conversation', payload: { conversationId: conversation.conversationId, isRead } })
            inboxDispatch({ type: 'update-selected-conversation-read-unread', payload: { isRead } })
            setToast({ type: 'success', message: `Conversation successfully ${isRead ? 'marked as read' : 'marked as unread'}.` })
        } catch {
            setToast({ type: 'error', message: `There was an error marking your conversation.` })
        }
    }

    const readConversationsHandler = async (isRead: boolean) => {
        const conversationIds = selectedConversations.map((c: any) => c.conversationId)
        try {
            await updateConversations({ type: 'read-conversations', conversations: selectedConversations.map((c: any) => ({ _id: c?._id, isRead })) })
            await Promise.allSettled(conversationIds.map((id: string) => inboxMarkAsSeen(id, isRead)))
            inboxDispatch({ type: 'update-conversations', payload: { conversationIds, conversationParameters: { isRead, isSelected: false } } })
            inboxDispatch({ type: 'set-selected-conversations', payload: [] })
            setToast({ type: 'success', message: `Conversations successfully ${isRead ? 'marked as read' : 'marked as unread'}.` })
        } catch {
            setToast({ type: 'error', message: `There was an error marking your conversations.` })
        }
    }

    // ===== ARCHIVE =====
    const archiveConversationHandler = async ({ id, conversationId, isArchived }: any) => {
        try {
            await updateConversations({ type: 'archive-conversation', conversations: [{ _id: id, isArchived }] })
            inboxArchiveConversation(conversationId, isArchived).catch(() => {})

            const updated = conversations
                .map((c: any) => c.conversationId === conversationId ? { ...c, isArchived } : c)
                .filter((c: any) => !(isArchived && archived === 'No' && c.isArchived))
            inboxDispatch({ type: 'set-conversations', payload: updated })
            if (selectedConversation?.conversationId === conversationId) {
                inboxDispatch({ type: 'update-selected-conversation', payload: { ...selectedConversation, isArchived } })
            }
            setToast({ type: 'success', message: `Conversation successfully ${isArchived ? 'archived' : 'unarchived'}.` })
        } catch {
            setToast({ type: 'error', message: `There was an error archiving your conversation.` })
        }
    }

    const archiveConversationsHandler = async (isArchived: boolean) => {
        const conversationIds = selectedConversations.map((c: any) => c.conversationId)
        try {
            await updateConversations({ type: 'archive-conversations', conversations: selectedConversations.map((c: any) => ({ _id: c?._id, isArchived })) })
            // Extension sync is best-effort — UI always updates from backend result
            inboxArchiveConversations(conversationIds, isArchived).catch(() => {})

            const updated = conversations
                .map((c: any) => conversationIds.includes(c.conversationId) ? { ...c, isArchived, isSelected: false } : c)
                // Remove from view when archiving and the filter shows only non-archived (default)
                .filter((c: any) => !(isArchived && archived === 'No' && c.isArchived))
            inboxDispatch({ type: 'set-conversations', payload: updated })
            inboxDispatch({ type: 'set-selected-conversations', payload: [] })
            setToast({ type: 'success', message: `Conversations successfully ${isArchived ? 'archived' : 'unarchived'}.` })
        } catch {
            setToast({ type: 'error', message: `There was an error archiving your conversations.` })
        }
    }

    // ===== DELETE =====
    const deleteConversationHandler = async (conversationId: string, conversationExistsOnLinkedIn: boolean) => {
        inboxDispatch({ type: 'set-modal', payload: { type: null } })
        setSpinner(true)
        try {
            if (conversationExistsOnLinkedIn) {
                await inboxDeleteConversation(conversationId)
            }
            await deleteConversationRequest(conversationId)
            setToast({ type: 'success', message: 'Conversation successfully deleted.' })

            const syncedConversations = conversations.filter((c: any) => c.conversationId !== conversationId)
            inboxDispatch({ type: 'set-conversations', payload: syncedConversations })

            if (selectedConversation?.conversationId === conversationId) {
                if (syncedConversations.length) {
                    openConversation(syncedConversations[0])
                } else {
                    inboxDispatch({ type: 'reset-selected-conversation' })
                }
            }
        } catch {
            setToast({ type: 'error', message: 'There was an error deleting the conversation.' })
        } finally {
            setSpinner(false)
        }
    }

    // ===== TAGS =====
    const leanObjectIdString = (ref: unknown): string => {
        if (ref == null) return ''
        if (typeof ref === 'string') return ref
        if (typeof ref === 'object' && ref !== null && '$oid' in ref && typeof (ref as { $oid: unknown }).$oid === 'string') {
            return (ref as { $oid: string }).$oid
        }
        if (typeof ref === 'object' && ref !== null && '_id' in ref) {
            return leanObjectIdString((ref as { _id: unknown })._id)
        }
        return String(ref)
    }

    const applyTagsToConversationsHandler = async (tagIds: any[], connectionIds: string[], shouldResetSelection = true, shouldRefetchTags = false) => {
        try {
            const conversationIds = conversations.filter((c: any) => connectionIds.includes(c.connectionId)).map((c: any) => c.conversationId)
            const result = await applyTagsRequest({ tags: tagIds, connectionIds, conversationIds, shouldRefetchTags })
            if (result?.conversations) {
                const updatedConvs = conversations.map((conv: any) => {
                    const updated = result.conversations.find((r: any) => r.conversationId === conv.conversationId)
                    return updated ? { ...conv, tags: updated.tags } : conv
                })
                inboxDispatch({ type: 'set-conversations', payload: updatedConvs })
                if (selectedConversation) {
                    const updatedSelected = result.conversations.find((r: any) => r.conversationId === selectedConversation.conversationId)
                    if (updatedSelected) inboxDispatch({ type: 'update-selected-conversation', payload: { ...selectedConversation, tags: updatedSelected.tags } })
                }
            }
            if (shouldResetSelection) inboxDispatch({ type: 'set-selected-conversations', payload: [] })
            if (shouldRefetchTags) getFilteringTagOptions()
        } catch {
            setToast({ type: 'error', message: 'There was a problem adding tags to your conversations.' })
        }
    }

    const removeTagsFromConversationsHandler = async (tagIds: string[], connectionIds: string[], shouldResetSelection = true) => {
        if (!connectionIds.length) {
            setToast({ type: 'error', message: 'There was a problem removing tags from your conversation.' })
            return
        }
        try {
            const conversationIds = conversations.map((c: any) => c.conversationId)
            await removeTagsRequest({ tags: tagIds, connectionIds, conversationIds })
            const tagIdSet = new Set(tagIds.map(String))
            const connectionIdSet = new Set(connectionIds.map(String))
            const updatedConvs = conversations.map((conv: any) => {
                if (connectionIdSet.has(String(conv.connectionId))) {
                    return { ...conv, tags: conv.tags?.filter((t: any) => !tagIdSet.has(String(t?._id))) }
                }
                return conv
            })
            inboxDispatch({ type: 'set-conversations', payload: updatedConvs })
            const selectedConnId = selectedConversation ? leanObjectIdString(selectedConversation.connectionId) : ''
            if (selectedConversation && selectedConnId && connectionIdSet.has(selectedConnId)) {
                inboxDispatch({ type: 'update-selected-conversation', payload: {
                    ...selectedConversation,
                    tags: selectedConversation.tags?.filter((t: any) => !tagIdSet.has(String(t?._id)))
                }})
            }
            const selectedConnectionId = selectedConnection ? leanObjectIdString(selectedConnection?._id) : ''
            if (selectedConnection && selectedConnectionId && connectionIdSet.has(selectedConnectionId)) {
                inboxDispatch({
                    type: 'set-selected-connection',
                    payload: {
                        ...selectedConnection,
                        tags: (selectedConnection.tags || []).filter((t: any) => !tagIdSet.has(String(t?._id))),
                    },
                })
            }
            if (shouldResetSelection) inboxDispatch({ type: 'set-selected-conversations', payload: [] })
        } catch {
            setToast({ type: 'error', message: 'There was a problem removing tags from your conversations.' })
        }
    }

    const applyAndRemoveTagsFromConversationHandler = async (tags: any, connectionIds: string[]) => {
        try {
            const conversationIds = conversations.filter((c: any) => connectionIds.includes(c.connectionId)).map((c: any) => c.conversationId)
            const result = await applyRemoveTagsRequest({
                tagsToAdd: tags.new.map((t: any) => t._id || { color: t.color, title: t.title || t.label, private: !!t.private, forKanban: !!t.forKanban, kanban: t.kanban || null }),
                tagsToRemove: tags.removed.map((t: any) => t._id),
                connectionIds,
                conversationIds
            })
            if (result?.conversations) {
                const updatedConvs = conversations.map((conv: any) => {
                    const updated = result.conversations.find((r: any) => r.conversationId === conv.conversationId)
                    return updated ? { ...conv, tags: updated.tags } : conv
                })
                inboxDispatch({ type: 'set-conversations', payload: updatedConvs })
                if (selectedConversation && connectionIds.includes(selectedConversation.connectionId)) {
                    const updatedSelected = result.conversations.find((r: any) => r.conversationId === selectedConversation.conversationId)
                    if (updatedSelected) inboxDispatch({ type: 'update-selected-conversation', payload: { ...selectedConversation, tags: updatedSelected.tags } })
                }
            }
        } catch {
            setToast({ type: 'error', message: 'There was an error with applying tags.' })
        }
    }

    const trimInboxLiSegment = (value: string) => value.replace(/^\/+|\/+$/g, '').trim()

    const addInboxParticipantToLeadDeltaHandler = async (): Promise<IConnection | null> => {
        const conv = selectedConversation
        const p0 = conv?.participants?.[0]
        if (!p0) {
            setToast({ type: 'error', message: 'No participant to add.' })
            return null
        }
        const firstName = p0.firstName || ''
        const lastName = p0.lastName || ''
        const fullName = `${firstName} ${lastName}`.trim()
        const pub = trimInboxLiSegment(String(p0.publicIdentifier || ''))
        const pid = trimInboxLiSegment(String(p0.profileId || p0.memberId || ''))
        if (!pub && !pid) {
            setToast({ type: 'error', message: 'Could not resolve LinkedIn profile for this person.' })
            return null
        }

        const profilePageUrl = pub ? `https://www.linkedin.com/in/${pub}/` : `https://www.linkedin.com/in/${pid}/`

        let resolvedPublic = pub
        let resolvedProfileId = pid
        try {
            const extracted: any = await extractLinkedinProfileHtml({
                pageUrl: profilePageUrl,
                includeHtml: false,
                ...(pid && !pub ? { profileId: pid } : {}),
            })
            if (extracted && typeof extracted === 'object' && extracted.success !== false && !extracted.error) {
                const ep = trimInboxLiSegment(String(extracted.publicIdentifier || ''))
                const eid = trimInboxLiSegment(String(extracted.profileId || ''))
                if (ep) resolvedPublic = ep
                if (eid) resolvedProfileId = eid
            }
        } catch {}

        const bodyToSend: Record<string, unknown> = {
            ...p0,
            name: fullName || p0.name,
            firstName,
            lastName,
            headline: p0.headline || p0.occupation,
            profilePicture: p0.profilePicture,
            country: p0.country,
            publicIdentifier: resolvedPublic || undefined,
            profileId: resolvedProfileId || undefined,
        }

        const applyConnectionToInbox = (connection: any) => {
            if (!connection?._id) return false
            inboxDispatch({ type: 'set-selected-connection', payload: connection })
            if (conv?.conversationId) {
                inboxDispatch({
                    type: 'update-selected-conversation',
                    payload: { ...conv, connectionId: connection._id },
                })
                inboxDispatch({
                    type: 'update-conversation',
                    payload: {
                        conversationId: conv.conversationId,
                        connectionId: connection._id,
                    },
                })
            }
            return true
        }

        try {
            const extResult: any = await ldAddNewConnection(bodyToSend)
            if (extResult && extResult.success === true && extResult.data) {
                if (applyConnectionToInbox(extResult.data)) {
                    setToast({ type: 'success', message: 'Contact successfully added to LeadDelta.' })
                    return extResult.data as IConnection
                }
            }

            /** Fallback: direktan API kao `/profiles/new` sa ispravnim `{ profile }` telom (backend očekuje `request.body.profile`) */
            let connection: any = await addToLeadDelta({ profile: bodyToSend, isProspect: false })
            if (connection && !connection._id && resolvedPublic) {
                try {
                    connection = await getConnectionByPublicIdentifier(resolvedPublic)
                } catch {
                    /* ostavi connection */
                }
            }
            if (!connection?._id) {
                setToast({ type: 'error', message: 'There was a problem adding your contact.' })
                return null
            }
            applyConnectionToInbox(connection)
            setToast({ type: 'success', message: 'Contact successfully added to LeadDelta.' })
            return connection as IConnection
        } catch {
            setToast({ type: 'error', message: 'There was a problem adding your contact.' })
            return null
        }
    }

    const addInboxParticipantAndEnrichHandler = async (flags: {
        privateEmailChecked: boolean
        businessEmailChecked: boolean
        phoneChecked: boolean
    }): Promise<boolean> => {
        if (!flags.privateEmailChecked && !flags.businessEmailChecked && !flags.phoneChecked) {
            return false
        }
        if ((workspace?.enrichmentCredits ?? 0) <= 0) {
            setToast({ type: 'error', message: 'No enrichment credits left.' })
            return false
        }
        const conn = await addInboxParticipantToLeadDeltaHandler()
        if (!conn?._id) {
            return false
        }
        try {
            const resolved = await resolveLinkedinProfileForInboxEnrichHandler(conn)
            inboxDispatch({ type: 'set-selected-connection', payload: resolved })
            const started = await runSingleConnectionEnrichmentHandler(resolved._id, flags)
            if (!started) {
                setToast({ type: 'error', message: 'Contact was added but enrichment could not be started.' })
            }
            return started
        } catch {
            setToast({ type: 'error', message: 'Contact was added but enrichment could not be started.' })
            return false
        }
    }

    const visitLinkedInConversationHandler = async (conversationId: string) => {
        try {
            await visitLinkedInConversation(conversationId)
        } catch {
            setToast({ type: 'error', message: 'There was a problem opening this conversation on LinkedIn.' })
        }
    }

    const visitParticipantLinkedInProfileHandler = (participant: any) => {
        const publicIdentifier = participant?.publicIdentifier
        if (publicIdentifier) {
            window.open(`https://www.linkedin.com/in/${publicIdentifier}`, '_blank', 'noopener,noreferrer')
        }
    }

    const visitParticipantLinkedInActivityFeedHandler = (participant: any) => {
        const publicIdentifier = participant?.publicIdentifier
        if (publicIdentifier) {
            window.open(`https://www.linkedin.com/in/${publicIdentifier}/recent-activity/all/`, '_blank', 'noopener,noreferrer')
        }
    }

    // ===== UI HELPERS =====
    const openNewMessagePage = () => {
        inboxDispatch({ type: 'set-selected-conversation', payload: null })
        inboxDispatch({ type: 'set-selected-profile', payload: null })
        inboxDispatch({ type: 'set-selected-connection', payload: null })
        inboxDispatch({ type: 'set-conversation-details', payload: false })
    }

    const setSelectedParticipants = (payload: any[]) => inboxDispatch({ type: 'set-selected-participants', payload })
    const setConnectionsSearchQueryName = (name: string) => inboxDispatch({ type: 'set-connections-search-query-name', payload: name })
    const setMessageAttachments = (files: any[]) => inboxDispatch({ type: 'set-message-attachments', payload: files })
    const setApplyTemplateHandler = (template: any) => inboxDispatch({ type: 'set-applied-template', payload: template })
    const setResetLastFetchedConversation = () => inboxDispatch({ type: 'set-last-fetched-conversation', payload: {} })
    const setResetSelectedConversations = () => {
        // Keep store + row checkbox state consistent.
        // Use the ref so we always clear the current conversations, not a stale closure snapshot
        // (this fires via a useEffect triggered by activeInboxLabel, at which point the closure
        // value of `conversations` may still refer to the previous tab's list).
        inboxDispatch({ type: 'set-selected-conversations', payload: [] })
        inboxDispatch({ type: 'set-conversations', payload: latestInboxStoreRef.current.conversations.map((c: any) => ({ ...c, isSelected: false })) })
    }
    const openConversationDetailsHandler = (payload: boolean) => inboxDispatch({ type: 'set-conversation-details', payload })

    // ===== FILTERS =====
    const setTagsFilter = (selectedTags: any[]) => inboxDispatch({ type: 'set-tags-filter', payload: selectedTags })
    const resetFilters = () => inboxDispatch({ type: 'reset-filters', payload: 'All' })
    const updateNumberOfFilters = () => {
        if (isSalesNavigatorActive) {
            let count = 0
            if (tags.length) count++
            inboxDispatch({ type: 'set-number-of-filters', payload: count })
            return
        }
        let count = 0
        if (pinned !== 'All') count++
        if (starred !== 'All') count++
        if (readed !== 'All') count++
        if (archived !== 'No') count++
        if (tags.length > 0) count++
        if (lastMessagedFrom !== '') count++
        if (lastMessagedTo !== '') count++
        if (conversationStatus?.value !== 'all') count++
        if (lastMessagedOnlyLast) count++
        if (hasConversationOwnerFilter()) count++
        inboxDispatch({ type: 'set-number-of-filters', payload: count })
    }

    const filterConversations = (convList: any[]): any[] => {
        /**
         * Sales Navigator: same idea as linkedinextension — sidebar filters are not all applicable;
         * inherited Inbox filter values would hide the whole SN list. Tags only (search is via LI API).
         */
        if (isSalesNavigatorActive) {
            const filtered = convList.filter((c: any) => {
                if (!c.isSales) return false
                const tagOk = !tags.length || (c.tags?.some((t: any) => tags.some((ft: any) => ft._id === t._id)))
                return tagOk
            })
            inboxDispatch({ type: 'set-filtered-conversations', payload: filtered })
            return filtered
        }

        const label = latestInboxStoreRef.current.activeInboxLabel
        const filters = [
            (c: any) => !isDismissedFromLabelTab(c.conversationId, label),
            (c: any) => conversationMatchesInboxLabel(c, label),
            (c: any) => pinned === 'All' || (pinned === 'Yes' ? c.isPinned : !c.isPinned),
            (c: any) => starred === 'All' || (starred === 'Yes' ? c.isStarred : !c.isStarred),
            (c: any) => readed === 'All' || (readed === 'All Read' ? c.isRead : !c.isRead),
            (c: any) => archived === 'All' || (archived === 'No' ? !c.isArchived : c.isArchived),
            (c: any) => !tags.length || (c.tags?.some((t: any) => tags.some((ft: any) => ft._id === t._id))),
        ]
        const selectedId = selectedConversation?.conversationId
        const keepSelectedDespiteFilters =
            !!selectedId && !isConversationTagInboxTab(label)
        const filtered = convList.filter((c: any) =>
            filters.every(f => f(c)) || (keepSelectedDespiteFilters && c.conversationId === selectedId)
        )
        inboxDispatch({ type: 'set-filtered-conversations', payload: filtered })
        return filtered
    }

    const getRemoveTagOptions = () => {
        const tagOptions: any[] = []
        selectedConversations.forEach((conv: any) =>
            conv.tags?.forEach((tag: any) => {
                if (tag.title !== 'Disconnected' && tag.title !== 'New Connection' && !tagOptions.some((t: any) => t._id === tag._id)) {
                    if (tag._id) tagOptions.push({ ...tag, value: tag._id, label: tag.title })
                }
            })
        )
        return tagOptions
    }

    // ===== AI TRAINING =====
    const trainAiConversationsHandler = async (opts?: { monthsBack?: number; maxMessages?: number }): Promise<{ success: boolean; count: number; message: string }> => {
        const monthsBack = opts?.monthsBack ?? 3
        const maxMessages = opts?.maxMessages ?? 30
        const threeMonthsAgo = moment().subtract(monthsBack, 'months').valueOf()
        const MAX_CONVERSATIONS = 50
        const MAX_PAGES = 5

        // Paginate through LeadDelta API to collect up to MAX_CONVERSATIONS
        let candidates: any[] = []
        try {
            for (let p = 1; p <= MAX_PAGES && candidates.length < MAX_CONVERSATIONS; p++) {
                const result: any = await paginateConversations({ page: p, isSales: false })
                const batch: any[] = result?.conversations ?? []
                if (!batch.length) break
                candidates = [...candidates, ...batch]
                if (result?.metadata?.hasNext === false) break
            }
        } catch (e) {
            console.warn('[trainAi] API pagination failed, falling back to store:', e)
        }

        // Fall back to store conversations if API returned nothing
        if (!candidates.length) {
            candidates = [...conversations]
        }

        // Filter to last N months, sort newest-first, cap
        const targets = candidates
            .sort((a: any, b: any) => (b.dateTimestamp || 0) - (a.dateTimestamp || 0))
            .filter((c: any) => !c.dateTimestamp || Number(c.dateTimestamp) >= threeMonthsAgo)
            .slice(0, MAX_CONVERSATIONS)

        if (!targets.length) {
            return { success: false, count: 0, message: 'No recent conversations found (last 3 months).' }
        }

        const archivePayload: any[] = []

        for (const conv of targets) {
            // Resolve the conversation ID — field name may differ between extension and backend formats
            const convId: string = conv.conversationId ?? conv.entityUrn ?? conv.id ?? ''
            if (!convId) continue

            try {
                // Fetch messages fresh from LinkedIn via extension
                let rawMessages: any = await getMessagesFromConversation(convId)
                let msgArray: any[] = Array.isArray(rawMessages) ? rawMessages : []

                // If first page is full (20) and we want more, fetch next page
                if (msgArray.length >= 20 && maxMessages > 20) {
                    const oldestTs = msgArray[msgArray.length - 1]?.dateTimestamp
                    if (oldestTs) {
                        const rawPage2: any = await getMessagesFromConversation(convId, oldestTs)
                        const page2: any[] = Array.isArray(rawPage2) ? rawPage2 : []
                        msgArray = [...msgArray, ...page2].slice(0, maxMessages)
                    }
                }

                if (!msgArray.length) continue

                // Map to backend format: sender must be 'user' or 'peer'
                const formattedMessages = msgArray
                    .slice(0, maxMessages)
                    .filter((m: any) => typeof m.text === 'string' && m.text.trim())
                    .map((m: any) => ({
                        sender: m.isSent ? 'user' : 'peer',
                        text: m.text.trim(),
                        ...(m.dateTimestamp ? { sentAt: new Date(Number(m.dateTimestamp)).toISOString() } : {}),
                    }))

                if (!formattedMessages.length) continue

                const peer = conv.participants?.[0]
                archivePayload.push({
                    threadKey: convId,
                    messages: formattedMessages,
                    peer: peer
                        ? {
                              displayName: [peer.firstName, peer.lastName].filter(Boolean).join(' ') || peer.publicIdentifier || '',
                              publicIdentifier: peer.publicIdentifier || undefined,
                          }
                        : undefined,
                    source: 'linkedin_inbox',
                })
            } catch (e) {
                console.error(`trainAi: failed fetching messages for ${convId}`, e)
            }
        }

        if (!archivePayload.length) {
            return { success: false, count: 0, message: 'No conversations had text messages to archive.' }
        }

        const result: any = await saveConversationArchive({ conversations: archivePayload })
        const count = result?.recorded ?? archivePayload.length
        return {
            success: !!result,
            count,
            message: `AI trained with ${count} conversation${count !== 1 ? 's' : ''}.`,
        }
    }

    const updateLastMessageInConversationList = (lastMessage: string) => {
        const conversationId = selectedConversation?.conversationId
        if (!conversationId) return

        const patch = {
            conversationId,
            text: lastMessage || 'Sent an attachment',
            dateTimestamp: moment().valueOf(),
            isSent: true,
        }

        const updatedConversations = conversations.map((conv: any) =>
            conv.conversationId === conversationId ? { ...conv, ...patch } : conv
        )
        inboxDispatch({ type: 'update-conversation', payload: patch })
        filterConversations(updatedConversations)
    }

    const markConversationTagRelabelPending = (conversationId: string) => {
        if (!conversationId) return

        const label = latestInboxStoreRef.current.activeInboxLabel
        if (isConversationTagInboxTab(label) && label) {
            awaitingRelabelOnTabRef.current.set(conversationId, label)
        }
        tagsBeforeRelabelRef.current.set(
            conversationId,
            getConversationTagLabel(selectedConversation?.conversationTags),
        )

        const patch = {
            conversationId,
            conversationTagPending: true,
            conversationTagRelabelFrom: getConversationTagLabel(selectedConversation?.conversationTags),
            conversationTagRelabelTab: isConversationTagInboxTab(label) ? label : undefined,
        }

        const updatedConversations = conversations.map((conv: any) =>
            conv.conversationId === conversationId ? { ...conv, ...patch } : conv
        )
        inboxDispatch({ type: 'update-conversation', payload: patch })
        filterConversations(updatedConversations)
    }

    const onConversationTagResolved = (row: {
        conversationId: string
        conversationTags: any[]
        conversationTagPending: boolean
    }): boolean => {
        const existingConv = latestInboxStoreRef.current.conversations.find(
            (c: any) => c.conversationId === row.conversationId,
        )
        const sourceLabelTab =
            awaitingRelabelOnTabRef.current.get(row.conversationId) ??
            (isConversationTagInboxTab(existingConv?.conversationTagRelabelTab)
                ? existingConv?.conversationTagRelabelTab
                : undefined)
        const beforeLabel =
            tagsBeforeRelabelRef.current.get(row.conversationId) ??
            existingConv?.conversationTagRelabelFrom ??
            (existingConv?.conversationTagPending
                ? getConversationTagLabel(existingConv?.conversationTags)
                : undefined)
        const requiresLabelChange =
            sourceLabelTab === 'ai:To respond' || beforeLabel === 'To respond'

        if (
            requiresLabelChange &&
            beforeLabel !== undefined &&
            isStaleRelabelTagStatus(beforeLabel, row)
        ) {
            return false
        }

        if (
            requiresLabelChange &&
            sourceLabelTab &&
            conversationMatchesInboxLabel({ ...existingConv, conversationTags: row.conversationTags }, sourceLabelTab)
        ) {
            return false
        }

        tagsBeforeRelabelRef.current.delete(row.conversationId)

        const label = latestInboxStoreRef.current.activeInboxLabel
        const wasAwaitingRelabel = sourceLabelTab
        awaitingRelabelOnTabRef.current.delete(row.conversationId)

        inboxDispatch({
            type: 'update-conversation',
            payload: {
                conversationId: row.conversationId,
                conversationTags: row.conversationTags,
                conversationTagPending: false,
                conversationTagRelabelFrom: undefined,
                conversationTagRelabelTab: undefined,
            },
        })

        const mergedConv = existingConv
            ? {
                ...existingConv,
                conversationTags: row.conversationTags,
                conversationTagPending: false,
                conversationTagRelabelFrom: undefined,
                conversationTagRelabelTab: undefined,
            }
            : null

        const leftCurrentLabelTab =
            isConversationTagInboxTab(label) &&
            !!label &&
            wasAwaitingRelabel === label &&
            mergedConv &&
            !conversationMatchesInboxLabel(mergedConv, label)

        const updatedConversations = latestInboxStoreRef.current.conversations.map((c: any) =>
            c.conversationId === row.conversationId
                ? {
                    ...c,
                    conversationTags: row.conversationTags,
                    conversationTagPending: false,
                    conversationTagRelabelFrom: undefined,
                    conversationTagRelabelTab: undefined,
                }
                : c
        )

        if (leftCurrentLabelTab && label) {
            dismissFromLabelTab(row.conversationId, label)

            const shouldAdvance =
                latestInboxStoreRef.current.selectedConversation?.conversationId === row.conversationId
            let nextConv: any = null
            if (shouldAdvance) {
                const currentList = latestInboxStoreRef.current.filteredConversations ?? []
                const idx = currentList.findIndex((c: any) => c.conversationId === row.conversationId)
                nextConv = idx >= 0 ? (currentList[idx + 1] ?? currentList[idx - 1] ?? null) : null
            }

            filterConversations(updatedConversations)

            if (shouldAdvance) {
                if (nextConv?.conversationId && nextConv.conversationId !== row.conversationId) {
                    void openConversation(nextConv)
                } else {
                    inboxDispatch({ type: 'set-selected-conversation', payload: null })
                    inboxDispatch({ type: 'set-selected-connection', payload: null })
                    inboxDispatch({ type: 'set-selected-profile', payload: null })
                }
            }
            return true
        }

        filterConversations(updatedConversations)
        return true
    }

    const openInboxSourceTabHandler = (tab: 'Inbox' | 'SalesNavigator') => {
        if (tab === selectedTab) return
        inboxDispatch({ type: 'clear-inbox-on-source-tab-change', payload: tab })
    }

    const setInboxSelectedConnectionHandler = (connection: IConnection | null) => {
        inboxDispatch({ type: 'set-selected-connection', payload: connection })
        if (connection?._id) {
            notifyInboxConnectionsUpdatedFromConnections([connection])
        }
    }

    return {
        loadSalesNavigatorUser,
        // conversations
        getConversationsFirstPage,
        getFilteredLDConversations,
        shouldFetchConversationsFromLinkedin,
        loadMoreConversations,
        loadMoreConversationsFromLinkedin,
        loadMoreSalesNavigatorSearchConversations,
        loadMoreAllMessagesSearchConversations,
        salesNavigatorSearchActive,
        allMessagesSearchActive,
        openConversation,
        getConnectionHandler,
        // messages
        getMessagesFirstPage,
        loadMoreMessages,
        sendMessage,
        resendMessage,
        createNewConversationHandler,
        // star/pin
        starConversationHandler,
        starConversationsHandler,
        pinConversationHandler,
        pinConversationsHandler,
        // snooze
        snoozeConversationHandler,
        snoozeConversationsHandler,
        // read
        readConversationHandler,
        readConversationsHandler,
        // archive
        archiveConversationHandler,
        archiveConversationsHandler,
        // delete
        deleteConversationHandler,
        // tags
        applyTagsToConversationsHandler,
        removeTagsFromConversationsHandler,
        applyAndRemoveTagsFromConversationHandler,
        // linkedin
        visitLinkedInConversationHandler,
        visitParticipantLinkedInProfileHandler,
        visitParticipantLinkedInActivityFeedHandler,
        addInboxParticipantToLeadDeltaHandler,
        addInboxParticipantAndEnrichHandler,
        setInboxSelectedConnectionHandler,
        // ui
        openNewMessagePage,
        setSelectedParticipants,
        setConnectionsSearchQueryName,
        setMessageAttachments,
        setApplyTemplateHandler,
        setResetLastFetchedConversation,
        setResetSelectedConversations,
        openConversationDetailsHandler,
        openInboxSourceTabHandler,
        // filters
        setTagsFilter,
        resetFilters,
        updateNumberOfFilters,
        filterConversations,
        getRemoveTagOptions,
        getFilteringTagOptions,
        updateLastMessageInConversationList,
        onConversationTagResolved,
        // ai training
        trainAiConversationsHandler,
    }
}
